import React, { useEffect, useMemo, useRef, useState } from "react";
import { Keyboard } from "@capacitor/keyboard";
import { fetchProtectedFile, getFileFetchUrl } from "../utils/fileUrlHelper";
import AvatarImg from "../components/AvatarImg";
import AvatarPreviewModal from "../components/AvatarPreviewModal";
import SecureMediaViewer from "../components/SecureMediaViewer";
import { readSensitiveItem, writeSensitiveItem } from "../utils/sensitiveSessionStorage";
import MediaActionIcon, { MediaActionLoading } from "../components/MediaActionIcon";
import RenewableFileImage from "../components/RenewableFileImage";
import AppFeatureIcon from "../components/AppFeatureIcon";
import AsyncActionButton from "../components/AsyncActionButton";
import FormValidationNotice, { useFormValidation } from "../components/FormValidationNotice";
import ProfilePhotoEditor from "../components/ProfilePhotoEditor";
import AVATAR_IMPORTS from "../assets/avatar/index";
import childrenTrackingAsset from "../assets/cocuktakibiasset.png";
import childGrowthAsset from "../assets/cocuk_gelisim-takibi.png";
import childHealthAsset from "../assets/cocuk_saglik.png";
import childEducationAsset from "../assets/cocuk_okul-egitim.png";
import childNutritionAsset from "../assets/cocuk_beslenme.png";
import childMemoryAsset from "../assets/cocuk_anilar.png";
import { useLanguage } from "../i18n/LanguageContext";
import { profilFotografiniEditorIcinHazirla } from "../services/profilePictureService";
import { isAndroidPlatform, isIOSPlatform } from "../utils/platformFeatures";
import { androidDosyaPaylas } from "../utils/androidFileShare";

import {
  createChildProfile,
  createChildRecord,
  deleteChildProfile,
  deleteChildProfilePicture,
  deleteChildRecord,
  getChildrenBundle,
  updateChildProfile,
  uploadChildProfilePicture,
} from "../services/childrenService";
import { createActivity, getActivityActor } from "../services/activityService";
import { appAlert, appConfirm } from "../components/AppDialog";
import { canManageChildMember, memberBloodType, memberUid } from "../utils/familyMemberAccess";
import {
  childTrackingRecordCount,
  healthRecordsForChild,
  recordsForChild,
} from "../utils/childTrackingCounts";
import {
  compareGrowthRecords,
  growthMetricEntries,
  growthNumber as toNumber,
  latestGrowthMetric,
} from "../utils/childGrowthMetrics";

const today = () => new Date().toISOString().slice(0, 10);

const normalizeDosyaTipi = (tip) => {
  const t = String(tip || "").toLowerCase().trim();
  if (!t) return "";
  const mimeMap = {
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/png": "png",
    "image/gif": "gif",
    "image/webp": "webp",
    "application/pdf": "pdf",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  };
  return mimeMap[t] || t.split(".").pop()?.split("/").pop() || t;
};

const dosyaIkonu = (tip) => {
  const tip2 = normalizeDosyaTipi(tip);
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(tip2)) return "🖼️";
  if (tip2 === "pdf") return "📄";
  if (["doc", "docx"].includes(tip2)) return "📝";
  if (["xls", "xlsx"].includes(tip2)) return "📊";
  return "📎";
};

const boyutFormatla = (b) => {
  const n = Number(b || 0);
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const safeDosyaAdi = (dosyaAdi, mimeType, fallback = "belge") => {
  const clean = String(dosyaAdi || fallback).replace(/[\\/:*?"<>|]+/g, "_");
  if (clean.includes(".")) return clean;
  const ext = normalizeDosyaTipi(mimeType);
  return ext ? `${clean}.${ext === "jpg" ? "jpg" : ext}` : clean;
};

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "");
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const fetchBelgeBlob = async (url) => {
  const fileUrl = getFileFetchUrl(url);
  if (!fileUrl) throw new Error("Dosya URL bulunamadı.");
  const res = await fetchProtectedFile(fileUrl, { method: "GET", cache: "no-store" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.blob();
};



const CHILDREN_BUNDLE_CACHE_PREFIX = "aile_children_bundle_v1";
const childrenBundleMemoryCache = new Map();

function normalizeChildrenBundle(data = {}) {
  return {
    children: Array.isArray(data.children) ? data.children : [],
    childRecords: Array.isArray(data.childRecords) ? data.childRecords : [],
    healthRecords: Array.isArray(data.healthRecords) ? data.healthRecords : [],
  };
}

function childrenBundleCacheKey(familyId) {
  return familyId ? `${CHILDREN_BUNDLE_CACHE_PREFIX}_${familyId}` : null;
}

function readChildrenBundleCache(familyId) {
  const key = childrenBundleCacheKey(familyId);
  if (!key) return null;
  if (childrenBundleMemoryCache.has(key)) return childrenBundleMemoryCache.get(key);
  try {
    const raw = readSensitiveItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const bundle = normalizeChildrenBundle(parsed?.data || parsed);
    childrenBundleMemoryCache.set(key, bundle);
    return bundle;
  } catch {
    return null;
  }
}

function writeChildrenBundleCache(familyId, data) {
  const key = childrenBundleCacheKey(familyId);
  if (!key) return;
  const bundle = normalizeChildrenBundle(data);
  childrenBundleMemoryCache.set(key, bundle);
  try {
    writeSensitiveItem(key, JSON.stringify({ savedAt: Date.now(), data: bundle }));
  } catch {}
}

const CHILD_UI = {
  "Çocuk":"Child", "Bebek":"Baby", "Evcil Hayvan":"Pet",
  "Evcil":"Pet", "Kız":"Girl", "Erkek":"Male", "Dişi":"Female", "Belirtilmedi":"Not specified", "Bilinmiyor":"Unknown",
  "Kedi":"Cat", "Köpek":"Dog", "Kuş":"Bird", "Balık":"Fish", "Tavşan":"Rabbit", "Kaplumbağa":"Turtle", "Diğer":"Other",
  "Anne sütü":"Breast milk", "Mama":"Formula", "Ek gıda":"Solid food", "Meyve püresi":"Fruit puree", "Sebze püresi":"Vegetable puree", "Yoğurt":"Yogurt", "Çorba":"Soup", "Su":"Water", "Gece beslenmesi":"Night feeding",
  "Kahvaltı":"Breakfast", "Ara Öğün":"Snack", "Öğle":"Lunch", "Akşam":"Dinner", "Gece":"Night", "Okul beslenmesi":"School meal",
  "Az":"Low", "Normal":"Normal", "İyi":"Good", "Çok iyi":"Very good", "1 kase":"1 bowl", "1 porsiyon":"1 portion",
  "Mutlu":"Happy", "Sakin":"Calm", "Huzursuz":"Restless", "Yorgun":"Tired", "Ağlamaklı":"Tearful", "Enerjik":"Energetic", "İştahsız":"No appetite", "Keyifsiz":"Low mood",
  "Sık uyandı":"Woke often", "Zor uyudu":"Had trouble sleeping", "Kabus gördü":"Had a nightmare", "Islak bez":"Wet diaper", "Kaka":"Poop", "Karışık":"Mixed", "Kuru":"Dry", "Pişik var":"Rash", "İshal":"Diarrhea", "Kabızlık":"Constipation",
  "Ateş":"Fever", "İlaç":"Medicine", "Aşı":"Vaccine", "Doktor kontrolü":"Doctor check", "Alerji":"Allergy", "Hastalık":"Illness", "Diş":"Tooth", "Rapor":"Report", "Acil durum":"Emergency",
  "İlk gülümseme":"First smile", "Başını tuttu":"Held head up", "Döndü":"Rolled over", "Oturdu":"Sat up", "Emekledi":"Crawled", "İlk adım":"First step", "İlk kelime":"First word", "Diş çıkardı":"Teething", "Sosyal gelişim":"Social development", "Davranış notu":"Behavior note",
  "Ödev":"Homework", "Sınav":"Exam", "Karne":"Report card", "Ders başarısı":"Academic performance", "Devamsızlık":"Absence", "Okul etkinliği":"School activity", "Öğretmen görüşmesi":"Teacher meeting", "Servis":"School bus", "Kitap okuma":"Reading",
  "Uyku":"Sleep", "Yemek":"Meal", "Ders":"Lesson", "Kitap":"Book", "Spor":"Sports", "Ekran süresi":"Screen time", "Sorumluluk":"Responsibility", "Oyun":"Play", "Duygu durumu":"Mood",
  "İç parazit":"Internal parasite", "Dış parazit":"External parasite", "Veteriner kontrolü":"Vet check", "Operasyon":"Operation", "Diş bakımı":"Dental care", "Çip":"Chip", "Tüy bakımı":"Grooming", "Banyo":"Bath", "Tırnak kesimi":"Nail trimming", "Kum temizliği":"Litter cleaning", "Kulak temizliği":"Ear cleaning", "Mama değişimi":"Food change", "Yürüyüş":"Walk", "Egzersiz":"Exercise", "Eğitim":"Training", "Davranış":"Behavior", "Sosyalleşme":"Socialization", "Tuvalet takibi":"Toilet tracking",
  "Doğum günü":"Birthday", "Okul":"School", "Gezi":"Trip", "Başarı":"Achievement", "Komik an":"Funny moment", "Özel an":"Special moment", "Fotoğraf":"Photo",
  "Yaş bilinmiyor":"Age unknown", "Yaş yok":"No age", "Yeni doğan":"Newborn", "aylık":"months old", "yaş":"years old", "Tarih yok":"No date",
  "Grafik için en az 2 kayıt gerekli.":"At least 2 records are required for the chart.", "Gelişim grafiği için en az 2 ölçüm kaydı gerekli.":"At least 2 measurement records are required for the growth chart.", "Son değer":"Latest value", "Son ölçümler":"Latest measurements", "Son kayıt":"Latest record", "Boy trendi":"Height trend", "Kilo trendi":"Weight trend", "Baş çevresi trendi":"Head circumference trend", "Boy değişimi":"Height change", "Kilo değişimi":"Weight change", "Baş değişimi":"Head change", "Kayıt sayısı":"Record count", "Kayıt":"Record", "Ölçüm yok":"No measurement", "Son değişim":"Latest change", "Ölçüm geçmişi":"Measurement history", "Yeni Profil Ekle":"Add New Profile", "Bebek, çocuk veya evcil hayvan için kalıcı takip profili oluştur.":"Create a permanent tracking profile for a baby, child, or pet.",
  "Evcil hayvan adı":"Pet name", "Ad":"First name", "Soyad":"Last name", "Tür: kedi, köpek...":"Type: cat, dog...", "Cinsiyet":"Gender", "Kan grubu":"Blood type", "Irk":"Breed", "Çip numarası":"Chip number", "Veteriner bilgisi":"Veterinary info", "Acil veteriner / telefon":"Emergency vet / phone", "Acil kişi / telefon":"Emergency contact / phone", "Alerjiler":"Allergies",

  "Evcil hayvanların için sağlık, aşı ve bakım takibi yap":"Track health, vaccination and care for your pets",
  "Baş çevresi":"Head circumference", "Ayakkabı no / beden":"Shoe no / size", "Aşı, parazit, veteriner...":"Vaccine, parasite, vet...", "Ateş, ilaç, aşı, doktor...":"Fever, medicine, vaccine, doctor...", "Değer / doz / ölçüm":"Value / dose / measurement",
  "Okul adı":"School name", "Sınıf":"Class", "Öğretmen":"Teacher", "Tarih":"Date", "Aktivite / davranış":"Activity / behavior", "Rutin türü":"Routine type", "Başlangıç / saat":"Start / time", "Süre / miktar":"Duration / amount", "Tepki / alerji / iştah":"Reaction / allergy / appetite",
  "Bakım türü":"Care type", "Detay / ürün / uygulama":"Detail / product / application", "Anı başlığı":"Memory title", "Veteriner":"Veterinarian", "Doktor / branş":"Doctor / specialty",
  "Gelişim Kaydı":"Growth Record", "Sağlık Kaydı":"Health Record", "Eğitim Kaydı":"Education Record", "Rutin Kaydı":"Routine Record", "Bez Kaydı":"Diaper Record", "Bakım Kaydı":"Care Record", "Detay yok":"No details", "Henüz eğitim kaydı yok.":"No education records yet.",
  "Bu profil aile üyesi olduğu için sağlık kayıtları mevcut Sağlık sayfasından otomatik çekilir. Bu alandan eklenen notlar çocuk takibi içinde kalır.":"Because this profile is a family member, health records are pulled automatically from the Health page. Notes added here stay inside child tracking.",
  "Tepki":"Reaction", "Tür bilgisi yok":"No type info", "Kan grubu yok":"No blood type", "Gelişim":"Growth", "Sağlık":"Health", "Anı":"Memory", "Profil Bilgileri":"Profile Info", "Boy, kilo, sağlık, veteriner ve özel notlar":"Height, weight, health, vet and special notes", "Tür / Irk":"Type / Breed", "Son Boy":"Last Height", "Çip No":"Chip No", "Kronik":"Chronic", "Acil kişi":"Emergency contact",
  "Aile Arşivi":"Family Archive", "Çocuk / Bebek / Evcil Takibi":"Child / Baby / Pet Tracking", "Gelişim, sağlık, eğitim, rutin ve evcil hayvan bakım kayıtlarını yıllar sonra bile takip et.":"Track growth, health, education, routines and pet care records even years later.", "Henüz profil yok. Sağ üstteki + butonu ile bebek, çocuk veya evcil hayvan ekleyebilirsin.":"No profile yet. You can add a baby, child or pet from the + button at the top right.", "takip kaydı":"tracking records",
  "Bu profil":"This profile", "silinecek.":"will be deleted.", "Bu işlem geri alınamaz. Bu profile ait tüm çocuk takip kayıtları da kalıcı olarak silinecek.":"This action cannot be undone. All child tracking records belonging to this profile will also be permanently deleted.", "Evet, Kalıcı Olarak Sil":"Yes, Delete Permanently",
  "Notlar":"Notes",
  "Boy":"Height",
  "Boy cm":"Height cm",
  "Kilo kg":"Weight kg",
  "Servis / etkinlik / detay":"Bus / activity / detail",
  "Ruh hali / kalite":"Mood / quality",
  "Mama / besin":"Food / feed",
  "Ne yedi?":"What did they eat?",
  "En az bir gelişim ölçümü":"At least one development measurement",
  "Kayıt türü / başlık":"Record type / title",
  "Miktar":"Amount",
  "Bez durumu":"Diaper status",
  "Saat":"Time",
  "Renk / durum":"Color / status",
  "Kategori":"Category",
  "Not":"Note",
  "ay":"months",
  "Öğün":"Meal",
  "Baş":"Head",
  "Profil Silinsin mi?":"Delete profile?", "Bu profil ve bağlı takip kayıtları silinecek. Bu işlem geri alınamaz.":"This profile and related tracking records will be deleted. This action cannot be undone.", "Silmekten Vazgeç":"Cancel delete", "Evet, Sil":"Yes, Delete",
  "Yükleniyor":"Loading", "Yok":"None", "yok":"none", "Orta":"Medium", "Kaydet":"Save", "Vazgeç":"Cancel", "İptal":"Cancel", "Henüz kayıt yok.":"No records yet.", "Fotoğrafı aç":"Open photo", "Sil":"Delete", "Çocuk profilleri":"Child profiles", "Evcil hayvan profili":"Pet profile", "Sınırsız çocuk profili":"Unlimited child profiles", "Sınırsız evcil hayvan profili":"Unlimited pet profiles", "Gelişim ve sağlık takibi":"Growth and health tracking", "Tüm modüllerde sınırsız kullanım":"Unlimited use in all modules", "Aşı ve veteriner takibi":"Vaccination and vet tracking", "Aktivite ve bakım geçmişi":"Activity and care history", "Aile üyesi":"Family member", "Manuel profil":"Manual profile", "Dosya yükleniyor":"File uploading",
  "Kadın":"Female", "Cinsiyet belirtilmedi":"Gender not specified", "Son Kilo":"Last Weight", "Kilo":"Weight", "Not yok":"No note", "Profili sil":"Delete profile", "Profili Sil":"Delete Profile",
  "+ Gelişim Kaydı":"+ Growth Record", "+ Sağlık Kaydı":"+ Health Record", "+ Eğitim Kaydı":"+ Education Record", "+ Rutin Kaydı":"+ Routine Record", "+ Aktivite Kaydı":"+ Activity Record", "+ Beslenme Kaydı":"+ Nutrition Record", "+ Bez Kaydı":"+ Diaper Record", "+ Bakım Kaydı":"+ Care Record", "+ Anı Ekle":"+ Add Memory",
  "Sağlık, veteriner, ilaç, aşı ve kontrol kayıtlarını yıllar sonra bakabileceğin şekilde burada tutabilirsin.":"You can keep health, vet, medicine, vaccine and check-up records here for future reference.",
  "Mini sağlık kasası":"Mini health vault", "Sağlık ve bakım kasası":"Health and care vault", "Muayene, ateş, ilaç, tahlil ve doktor notlarını güvenli şekilde takip et.":"Track checkups, fever, medicines, tests and doctor notes safely.", "Aşı, veteriner ve bakım notlarını tek yerde düzenli tut.":"Keep vaccines, vet visits and care notes organized in one place.", "Güvende":"Safe", "Son kayıt":"Latest record", "Toplam":"Total", "Belgeli":"With files", "Son başlık":"Latest title", "Tümü":"All", "Muayene":"Checkup", "Ateş":"Fever", "İlaç":"Medicine", "Tahlil":"Lab", "Not":"Note", "Veteriner":"Vet", "Bu profil aile üyesi olduğu için sağlık sayfasındaki kayıtlar ve burada eklenen notlar birlikte gösterilir.":"Because this profile is a family member, Health page records and notes added here are shown together.", "Sağlık, aşı, ilaç ve kontrol kayıtlarını yıllar sonra bile rahatça bulabileceğin şekilde burada saklayabilirsin.":"Keep health, vaccine, medicine and checkup records here so you can find them easily even years later.", "Kayıt türü":"Record type", "Tarih":"Date", "Detay":"Detail", "Dosyalı kayıt":"File attached", "Ek önizlemeyi aç":"Open attachment preview", "Önizleme":"Preview", "Tap to preview":"Tap to preview", "Belge önizle":"Preview file", "Tap to preview document":"Tap to preview document",

  "Bu profil aile üyesi olduğu için sağlık kayıtları mevcut Sağlık sayfasından otomatik çekilir. Bu alandan eklenen notlar ayrıca çocuk geçmişine kaydedilir.":"Because this profile is a family member, health records are pulled automatically from the Health page. Notes added here are also saved in the child history.",
  "Eğitim kasası":"Education vault", "Okul, öğretmen, servis ve başarı notlarını düzenli tut.":"Keep school, teacher, transport and achievement notes organized.", "Rutin kasası":"Routine vault", "Uyku, duygu durumu, aktivite ve günlük alışkanlıkları takip et.":"Track sleep, mood, activities and daily habits.", "Beslenme kasası":"Nutrition vault", "Öğün, miktar, iştah ve alerji tepkilerini güvenle kaydet.":"Safely record meals, amounts, appetite and allergy reactions.", "Anı kasası":"Memory vault", "Fotoğraf, özel an ve minik başarıları sıcak bir arşivde sakla.":"Keep photos, special moments and little wins in a warm archive.", "Son aktivite":"Latest activity", "Son okul":"Latest school", "Son öğün":"Latest meal", "Son anı":"Latest memory", "Bu alanda okul, rutin, beslenme ve anı kayıtları sağlık kartıyla aynı premium düzende gösterilir.":"School, routine, nutrition and memory records are shown here with the same premium layout as the health card.", "Kayıt bulunamadı":"No records found", "Başlık":"Title", "Öğretmen":"Teacher", "Servis":"Transport", "Süre":"Duration", "Ruh hali":"Mood", "Yemek":"Meal", "Miktar":"Amount", "Dosya":"File",
  "Dosya seç":"Choose file", "Dosya seçilmedi":"No file selected", "Seçilen dosya":"Selected file", "Doğum tarihi":"Birth date",
  "Fotoğraf yükleniyor":"Uploading photo", "Kayıt güvenle oluşturuluyor":"Creating record securely", "Profil güvenle oluşturuluyor":"Creating profile securely", "Lütfen bekleyin":"Please wait", "Kayıt kaydedilemedi. Lütfen tekrar deneyin.":"The record could not be saved. Please try again.", "Profil kaydedilemedi. Lütfen tekrar deneyin.":"The profile could not be saved. Please try again.",
  "Profil ekle":"Add profile", "İlk profili oluştur":"Create the first profile", "Aile hikâyesini burada başlat":"Start your family story here", "Bebek, çocuk veya evcil hayvanın için güvenli bir profil oluştur; gelişimden anılara kadar her şeyi tek yerde düzenli tut.":"Create a secure profile for your baby, child or pet and keep everything from growth to memories organized in one place.", "Gelişim takibi":"Growth tracking", "Sağlık ve rutin":"Health and routines", "Anılar":"Memories"
};
const cu = (lang, text) => (lang === "en" ? (CHILD_UI[text] || text) : text);
const childCanonical = (text) => {
  const raw = String(text || "");
  const found = Object.entries(CHILD_UI).find(([, en]) => String(en).toLowerCase() === raw.toLowerCase());
  return found ? found[0] : raw;
};
const PROFILE_TYPES = {
  baby: { label: "Bebek", icon: "🍼", color: "#f59e0b", empty: "Bebek" },
  child: { label: "Çocuk", icon: "🧒", color: "#60a5fa", empty: "Çocuk" },
  pet: { label: "Evcil Hayvan", icon: "🐾", color: "#22c55e", empty: "Evcil" },
};

const CHILD_SECTION_ASSETS = {
  growth: childGrowthAsset,
  health: childHealthAsset,
  education: childEducationAsset,
  nutrition: childNutritionAsset,
  memory: childMemoryAsset,
};

/* Profil tipine göre seçilebilir avatar dosyaları */
const CHILD_AVATAR_MAP = {
  baby:  ["18bebek.png", "19bebekkiz.png"],
  child: ["14cocuk2.png", "15cocuk4.png", "16cocuk1.png", "17cocuk3.png"],
  pet:   ["20pet.png"],
};

/* Avatar dosyası → statik import URL */
const CHILD_AVATAR_IMPORTS = {
  "14cocuk2.png":   AVATAR_IMPORTS["14cocuk2.png"],
  "15cocuk4.png":   AVATAR_IMPORTS["15cocuk4.png"],
  "16cocuk1.png":   AVATAR_IMPORTS["16cocuk1.png"],
  "17cocuk3.png":   AVATAR_IMPORTS["17cocuk3.png"],
  "18bebek.png":    AVATAR_IMPORTS["18bebek.png"],
  "19bebekkiz.png": AVATAR_IMPORTS["19bebekkiz.png"],
  "20pet.png":      AVATAR_IMPORTS["20pet.png"],
};

/**
 * Avatar seçilmemişse profil tipine + cinsiyete göre sabit (deterministik) bir default döner.
 * id'nin son karakteri seed olarak kullanılır — aynı çocuk her zaman aynı avatarı görür.
 */
const getDefaultChildAvatar = (child) => {
  // Kalıcı dosya referansını, süresi dolabilen önizleme URL'sinden önce kullan.
  // Eski kayıt alanları geriye uyumluluk için korunur.
  if (child?.profilePictureUrl) return child.profilePictureUrl;
  if (child?.profilePictureFileKey && child?.avatarKind === "custom" && child?.avatar) return child.avatar;
  if (child?.avatar) return child.avatar;
  if (child?.avatarPreviewUrl) return child.avatarPreviewUrl;
  const type    = child?.profileType || child?.type || "child";
  const gender  = (child?.gender || "").toLowerCase();
  const isFemale = ["kız", "kadın", "kiz", "female", "f"].some(g => gender.includes(g));
  const isMale   = ["erkek", "male", "m"].some(g => gender.includes(g));
  const seed    = parseInt((child?.id || child?.memberUid || "0").slice(-2), 16) || 0;

  if (type === "pet") return "20pet.png";

  if (type === "baby") {
    if (isFemale) return "19bebekkiz.png";
    if (isMale)   return "18bebek.png";
    return seed % 2 === 0 ? "18bebek.png" : "19bebekkiz.png";
  }

  /* child — 4 seçenek, cinsiyet yok, seed'e göre sabit */
  const childList = CHILD_AVATAR_MAP.child;
  return childList[seed % childList.length];
};

const TYPE_BY_ROLE = { Bebek: "baby", Çocuk: "child" };

const calcAge = (date, type = "child", t) => {
  const _t = (k, fb) => (typeof t === "function" ? t(k) : fb || k);
  if (!date) return type === "pet" ? _t("children.ageUnknown", "Yaş bilinmiyor") : _t("health.noAge", "Yaş yok");
  const birth = new Date(date);
  const now = new Date();
  let years = now.getFullYear() - birth.getFullYear();
  let months = now.getMonth() - birth.getMonth();
  if (now.getDate() < birth.getDate()) months -= 1;
  if (months < 0) { years -= 1; months += 12; }
  if (years <= 0) return months <= 0 ? _t("children.newborn", "Yeni doğan") : `${months} ${_t("children.monthsOld", "aylık")}`;
  return months > 0 ? `${years} ${_t("children.yearsOld", "yaş")} ${months} ${_t("children.months", "ay")}` : `${years} ${_t("children.yearsOld", "yaş")}`;
};

const fullName = (p) => `${p?.ad || p?.name || ""} ${p?.soyad || p?.surname || ""}`.trim();

const childInitial = (child) => {
  const type = child.profileType || child.type || "child";
  if (type === "pet") return PROFILE_TYPES.pet.icon;
  const name = child.displayName || child.name || fullName(child);
  return name?.slice(0, 1)?.toUpperCase() || PROFILE_TYPES[type]?.empty?.[0] || "Ç";
};

const formatDate = (v, lang = "tr") => {
  if (!v) return cu(lang, "Tarih yok");
  try { return new Date(v).toLocaleDateString(lang === "en" ? "en-US" : "tr-TR"); } catch { return v; }
};

const isImageFile = (fileName = "", fileType = "") => {
  const t = String(fileType || fileName.split(".").pop() || "").toLowerCase();
  return ["jpg", "jpeg", "png", "gif", "webp", "heic", "heif"].includes(t);
};

const getProfileType = (child) => child?.profileType || child?.type || TYPE_BY_ROLE[child?.role] || "child";

const SMART_OPTIONS = {
  profileType: ["Bebek", "Çocuk", "Evcil Hayvan"],
  gender: ["Kız", "Erkek", "Dişi", "Erkek", "Belirtilmedi"],
  bloodType: ["0 Rh+", "0 Rh-", "A Rh+", "A Rh-", "B Rh+", "B Rh-", "AB Rh+", "AB Rh-", "Bilinmiyor"],
  petType: ["Kedi", "Köpek", "Kuş", "Balık", "Tavşan", "Hamster", "Kaplumbağa", "Diğer"],
  babyMeal: ["Anne sütü", "Mama", "Ek gıda", "Meyve püresi", "Sebze püresi", "Yoğurt", "Çorba", "Su", "Gece beslenmesi"],
  meal: ["Kahvaltı", "Ara Öğün", "Öğle", "Akşam", "Gece", "Okul beslenmesi"],
  amount: ["Az", "Normal", "İyi", "Çok iyi", "50 ml", "100 ml", "150 ml", "200 ml", "1 kase", "1 porsiyon"],
  mood: ["Mutlu", "Sakin", "Huzursuz", "Yorgun", "Ağlamaklı", "Enerjik", "İştahsız", "Keyifsiz"],
  sleepQuality: ["Çok iyi", "İyi", "Orta", "Sık uyandı", "Zor uyudu", "Kabus gördü"],
  diaperType: ["Islak bez", "Kaka", "Karışık", "Kuru", "Pişik var", "İshal", "Kabızlık"],
  healthType: ["Ateş", "İlaç", "Aşı", "Doktor kontrolü", "Alerji", "Hastalık", "Diş", "Rapor", "Acil durum"],
  development: ["İlk gülümseme", "Başını tuttu", "Döndü", "Oturdu", "Emekledi", "İlk adım", "İlk kelime", "Diş çıkardı", "Sosyal gelişim", "Davranış notu"],
  education: ["Ödev", "Sınav", "Karne", "Ders başarısı", "Devamsızlık", "Okul etkinliği", "Öğretmen görüşmesi", "Servis", "Kitap okuma"],
  routine: ["Uyku", "Yemek", "Ders", "Kitap", "Spor", "Ekran süresi", "Sorumluluk", "Oyun", "Duygu durumu"],
  petHealth: ["Aşı", "İç parazit", "Dış parazit", "Veteriner kontrolü", "İlaç", "Hastalık", "Operasyon", "Diş bakımı", "Çip"],
  petCare: ["Tüy bakımı", "Banyo", "Tırnak kesimi", "Diş bakımı", "Kum temizliği", "Kulak temizliği", "Mama değişimi"],
  petActivity: ["Yürüyüş", "Oyun", "Egzersiz", "Eğitim", "Davranış", "Sosyalleşme", "Tuvalet takibi"],
  memory: ["İlk adım", "İlk kelime", "Doğum günü", "Okul", "Gezi", "Başarı", "Komik an", "Özel an", "Fotoğraf"],
};

function SmartSelectInput({ value, onChange, options = [], placeholder, className = "", type = "text", lang = "tr" }) {
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    const q = String(value || "").toLocaleLowerCase("tr-TR");
    return options.filter((o) => String(o).toLocaleLowerCase("tr-TR").includes(q) || String(cu(lang, o)).toLocaleLowerCase("tr-TR").includes(q)).slice(0, 8);
  }, [value, options, lang]);

  return (
    <div className={`smartSelectWrap ${className}`}>
      <input
        type={type}
        value={lang === "en" ? cu(lang, value || "") : (value || "")}
        placeholder={placeholder}
        onChange={(e) => { onChange(childCanonical(e.target.value)); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 140)}
      />
      <button type="button" className="smartSelectBtn" onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen((p) => !p)}>⌄</button>
      {open && filtered.length > 0 && (
        <div className="smartSelectMenu">
          {filtered.map((opt) => (
            <button
              type="button"
              key={opt}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange(opt); setOpen(false); }}
            >
              {cu(lang, opt)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MiniLineChart({ records = [], field, suffix, lang = "tr" }) {
  const list = [...records]
    .filter((r) => r[field])
    .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")))
    .slice(-8);

  if (list.length < 2) return <div className="childrenEmptyMini">{cu(lang, "Grafik için en az 2 kayıt gerekli.")}</div>;

  const values = list.map((r) => toNumber(r[field]));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values.map((v, i) => `${(i / (values.length - 1)) * 280},${90 - ((v - min) / range) * 70}`).join(" ");

  return (
    <div className="childrenChartBox">
      <svg viewBox="0 0 280 110" className="childrenChart">
        <polyline fill="none" stroke="#60a5fa" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" points={points} />
        {points.split(" ").map((p, i) => {
          const [x, y] = p.split(",");
          return <circle key={i} cx={x} cy={y} r="4" fill="#ffffff" stroke="#60a5fa" strokeWidth="3" />;
        })}
      </svg>
      <div className="childrenChartFooter"><span>{cu(lang, "Son değer")}</span><b>{values[values.length - 1]} {suffix}</b></div>
    </div>
  );
}

function PremiumGrowthPanel({ records = [], lang = "tr" }) {
  const measurements = [...records]
    .filter((r) => toNumber(r.height) || toNumber(r.weight) || toNumber(r.head))
    .sort(compareGrowthRecords);

  const latest = measurements[measurements.length - 1] || null;
  const metrics = [
    { key: "height", label: cu(lang, "Boy"), suffix: "cm", className: "height" },
    { key: "weight", label: cu(lang, "Kilo"), suffix: "kg", className: "weight" },
    { key: "head", label: cu(lang, "Baş"), suffix: "cm", className: "head" },
  ];

  const formatMeasure = (key, suffix) => {
    const value = latestGrowthMetric(measurements, key)?.value;
    return value == null ? "—" : `${value} ${suffix}`;
  };
  const formatDelta = (key, suffix) => {
    const series = growthMetricEntries(measurements, key);
    const lastValue = series[series.length - 1]?.value;
    const prevValue = series[series.length - 2]?.value;
    if (lastValue == null || prevValue == null) return "—";
    const diff = Number((lastValue - prevValue).toFixed(1));
    if (!diff) return `0 ${suffix}`;
    return `${diff > 0 ? "+" : ""}${diff} ${suffix}`;
  };
  const metricSeries = (key) => growthMetricEntries(measurements, key).slice(-8).map((entry, i) => ({
    i,
    value: entry.value,
    date: entry.record.date,
  }));
  const buildSparkPoints = (key) => {
    const series = metricSeries(key);
    if (series.length < 2) return "";
    const values = series.map((p) => p.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    return series.map((p, idx) => `${10 + (idx / Math.max(series.length - 1, 1)) * 148},${48 - ((p.value - min) / range) * 34}`).join(" ");
  };
  const buildAreaPoints = (key) => {
    const points = buildSparkPoints(key);
    if (!points) return "";
    return `10,54 ${points} 158,54`;
  };

  if (measurements.length < 2) {
    return (
      <div className="childrenGrowthPremium childrenGrowthEmptyState">
        <div className="childrenGrowthPremiumHead">
          <div><span>📈</span><b>{cu(lang, "Son ölçümler")}</b></div>
          <em>{cu(lang, "Kayıt sayısı")}: {records.length}</em>
        </div>
        <div className="childrenEmptyMini">{cu(lang, "Gelişim grafiği için en az 2 ölçüm kaydı gerekli.")}</div>
      </div>
    );
  }

  return (
    <div className="childrenGrowthPremium">
      <div className="childrenGrowthPremiumHead">
        <div><span>📈</span><b>{cu(lang, "Son ölçümler")}</b></div>
        <em>{cu(lang, "Son kayıt")}: {formatDate(latest?.date, lang)}</em>
      </div>

      <div className="childrenGrowthStats compact">
        {metrics.map((m) => (
          <div key={m.key} className={`childrenGrowthStat ${m.className}`}>
            <span>{m.label}</span>
            <b>{formatMeasure(m.key, m.suffix)}</b>
            <small>{formatDelta(m.key, m.suffix)}</small>
          </div>
        ))}
      </div>

      <div className="childrenGrowthChartWrap childrenGrowthLanes" aria-label={cu(lang, "Gelişim")}>
        <div className="childrenGrowthLanesHead">
          <b>{cu(lang, "Son değişim")}</b>
          <span>{measurements.length} {cu(lang, "Kayıt")}</span>
        </div>
        {metrics.map((m) => {
          const points = buildSparkPoints(m.key);
          const area = buildAreaPoints(m.key);
          const last = latestGrowthMetric(measurements, m.key)?.value ?? null;
          const delta = formatDelta(m.key, m.suffix);
          const pointList = points ? points.split(" ") : [];
          const lastPoint = pointList.length ? pointList[pointList.length - 1].split(",") : null;
          return (
            <div key={m.key} className={`childrenGrowthLane ${m.className}`}>
              <div className="childrenGrowthLaneInfo">
                <span>{m.label}</span>
                <b>{last == null ? "—" : `${last} ${m.suffix}`}</b>
                <small>{delta}</small>
              </div>
              <svg viewBox="0 0 168 62" className="childrenGrowthSpark" role="img" aria-label={m.label}>
                <line x1="10" x2="158" y1="54" y2="54" className="baseline" />
                <line x1="10" x2="158" y1="14" y2="14" className="guide" />
                {area && <polygon points={area} className="area" />}
                {points && <polyline points={points} className="line" fill="none" />}
                {lastPoint && <circle cx={lastPoint[0]} cy={lastPoint[1]} r="4.2" className="lastDot" />}
              </svg>
            </div>
          );
        })}
        <div className="childrenGrowthDateRange">
          <span>{formatDate(measurements[0]?.date, lang)}</span>
          <span>{formatDate(latest?.date, lang)}</span>
        </div>
      </div>
    </div>
  );
}

function ChildrenSaveStatus({ label, progress = null, lang }) {
  const hasProgress = Number.isFinite(progress);
  const safeProgress = hasProgress ? Math.max(0, Math.min(100, Math.round(progress))) : null;
  return (
    <div className="childrenSaveStatus" role="status" aria-live="polite">
      <div className="childrenSaveStatusTop">
        <span className="childrenSaveSpinner" aria-hidden="true" />
        <div><b>{label}</b><small>{cu(lang, "Lütfen bekleyin")}</small></div>
        {hasProgress && <strong>%{safeProgress}</strong>}
      </div>
      <div className={`childrenSaveTrack${hasProgress ? "" : " isIndeterminate"}`} aria-hidden="true">
        <span style={hasProgress ? { width: `${Math.max(4, safeProgress)}%` } : undefined} />
      </div>
    </div>
  );
}

function useChildrenKeyboardViewport() {
  useEffect(() => {
    const viewport = window.visualViewport;
    const root = document.documentElement;
    let frameId = 0;
    let settleTimers = [];
    let nativeKeyboardHeight = 0;
    let disposed = false;
    let nativeHandles = [];
    let baselineHeight = Math.max(window.innerHeight || 0, viewport?.height || 0);

    const activeField = () => {
      const active = document.activeElement;
      if (!(active instanceof HTMLElement)) return null;
      if (!active.matches("input, textarea, select, [contenteditable='true']")) return null;
      return active.closest(".childrenPage, .childrenDetailPageWrapper, .childrenOverlay") ? active : null;
    };

    const visibleMetrics = () => {
      if (nativeKeyboardHeight > 0) {
        return {
          top: 0,
          height: Math.max(240, baselineHeight - nativeKeyboardHeight),
          inset: nativeKeyboardHeight,
        };
      }
      const height = viewport?.height || window.innerHeight || baselineHeight;
      const top = viewport?.offsetTop || 0;
      return {
        top,
        height,
        inset: Math.max(0, baselineHeight - height - top),
      };
    };

    const scrollFieldIntoView = () => {
      const field = activeField();
      if (!field) return;
      const metrics = visibleMetrics();

      // iOS resize:none altında WebKit en yakın kaydırılabilir atayı her zaman
      // kendisi seçemiyor. Önce doğal hizalamayı iste, ardından gerçek klavye
      // üst sınırına göre kesin piksel düzeltmesini uygula.
      try { field.scrollIntoView({ block: "center", inline: "nearest", behavior: "auto" }); } catch (_) {}

      const rect = field.getBoundingClientRect();
      const topLimit = metrics.top + 16;
      const bottomLimit = metrics.top + metrics.height - 20;
      let delta = 0;
      if (rect.bottom > bottomLimit) delta = rect.bottom - bottomLimit + 16;
      else if (rect.top < topLimit) delta = rect.top - topLimit - 16;
      if (!delta) return;

      let scroller = field.parentElement;
      while (scroller && scroller !== document.body) {
        const overflowY = window.getComputedStyle(scroller).overflowY;
        if (/(auto|scroll)/.test(overflowY) && scroller.scrollHeight > scroller.clientHeight + 2) break;
        scroller = scroller.parentElement;
      }
      if (scroller && scroller !== document.body) {
        scroller.scrollTop += delta;
      } else {
        const pageScroller = document.scrollingElement || document.documentElement;
        pageScroller.scrollTop += delta;
      }
    };

    const updateViewport = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        const field = activeField();
        if (!field && nativeKeyboardHeight <= 0) {
          baselineHeight = Math.max(baselineHeight, window.innerHeight || 0, viewport?.height || 0);
        }
        const metrics = visibleMetrics();
        const viewportKeyboardOpen = !!field && metrics.inset > 80 && Math.abs((viewport?.scale || 1) - 1) < 0.05;
        const keyboardOpen = !!field && (nativeKeyboardHeight > 0 || viewportKeyboardOpen);
        root.style.setProperty("--children-vv-height", `${Math.round(metrics.height)}px`);
        root.style.setProperty("--children-vv-top", `${Math.round(metrics.top)}px`);
        root.style.setProperty("--children-keyboard-inset", `${Math.round(metrics.inset)}px`);
        root.classList.toggle("childrenKeyboardOpen", keyboardOpen);
        if (keyboardOpen) scrollFieldIntoView();
      });
    };

    const scheduleSettledUpdate = () => {
      updateViewport();
      settleTimers.forEach((timer) => window.clearTimeout(timer));
      settleTimers = [60, 180, 360].map((delay) => window.setTimeout(() => {
        if (disposed) return;
        updateViewport();
        scrollFieldIntoView();
      }, delay));
    };

    const closeKeyboardLayout = () => {
      nativeKeyboardHeight = 0;
      settleTimers.forEach((timer) => window.clearTimeout(timer));
      settleTimers = [];
      root.classList.remove("childrenKeyboardOpen");
      root.style.setProperty("--children-keyboard-inset", "0px");
      root.style.setProperty("--children-vv-height", `${Math.round(viewport?.height || window.innerHeight || baselineHeight)}px`);
      root.style.setProperty("--children-vv-top", `${Math.round(viewport?.offsetTop || 0)}px`);
    };

    const orientationChanged = () => {
      closeKeyboardLayout();
      window.setTimeout(() => {
        if (disposed) return;
        baselineHeight = Math.max(window.innerHeight || 0, viewport?.height || 0);
        updateViewport();
      }, 220);
    };

    const installNativeListeners = async () => {
      if (!isIOSPlatform()) return;
      const showHandle = await Keyboard.addListener("keyboardWillShow", (info) => {
        nativeKeyboardHeight = Math.max(0, Math.round(Number(info?.keyboardHeight) || 0));
        scheduleSettledUpdate();
      });
      const hideHandle = await Keyboard.addListener("keyboardWillHide", closeKeyboardLayout);
      if (disposed) {
        showHandle.remove();
        hideHandle.remove();
        return;
      }
      nativeHandles = [showHandle, hideHandle];
    };

    viewport?.addEventListener("resize", scheduleSettledUpdate);
    viewport?.addEventListener("scroll", updateViewport);
    document.addEventListener("focusin", scheduleSettledUpdate);
    document.addEventListener("focusout", updateViewport);
    window.addEventListener("orientationchange", orientationChanged);
    installNativeListeners().catch(() => {});
    updateViewport();

    return () => {
      disposed = true;
      cancelAnimationFrame(frameId);
      settleTimers.forEach((timer) => window.clearTimeout(timer));
      nativeHandles.forEach((handle) => handle.remove());
      viewport?.removeEventListener("resize", scheduleSettledUpdate);
      viewport?.removeEventListener("scroll", updateViewport);
      document.removeEventListener("focusin", scheduleSettledUpdate);
      document.removeEventListener("focusout", updateViewport);
      window.removeEventListener("orientationchange", orientationChanged);
      root.classList.remove("childrenKeyboardOpen");
      root.style.removeProperty("--children-vv-height");
      root.style.removeProperty("--children-vv-top");
      root.style.removeProperty("--children-keyboard-inset");
    };
  }, []);
}

function ChildForm({ onClose, onSave }) {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({
    profileType: "child", name: "", surname: "", birthDate: "", gender: "", bloodType: "",
    petType: "", breed: "", chipNo: "", vetInfo: "", allergies: "", chronicDiseases: "",
    regularMedicines: "", emergencyContact: "", note: "",
  });
  const [saving, setSaving] = useState(false);
  const saveInFlightRef = useRef(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const typeMeta = PROFILE_TYPES[form.profileType] || PROFILE_TYPES.child;

  const save = async () => {
    if (saveInFlightRef.current) return;
    if (!form.name.trim()) { appAlert(t("children.nameRequired")); return; }
    saveInFlightRef.current = true;
    setSaving(true);
    try {
      await onSave(form);
    } catch (error) {
      console.error("[ChildrenPage] Profil kaydedilemedi:", error?.code || error?.message);
      throw error;
    } finally {
      saveInFlightRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="childrenOverlay" onClick={() => !saving && onClose()}>
      <div className="childrenModal childrenModalWide" onClick={(e) => e.stopPropagation()} aria-busy={saving}>
        <div className="childrenModalHead">
          <div><h3>{typeMeta.icon} {cu(lang, "Yeni Profil Ekle")}</h3><p>{cu(lang, "Bebek, çocuk veya evcil hayvan için kalıcı takip profili oluştur.")}</p></div>
          <button onClick={onClose} disabled={saving}>×</button>
        </div>

        <div className="childrenTypePicker">
          {Object.entries(PROFILE_TYPES).map(([key, meta]) => (
            <button key={key} type="button" className={form.profileType === key ? "active" : ""} onClick={() => set("profileType", key)}>
              <span>{meta.icon}</span><b>{cu(lang, meta.label)}</b>
            </button>
          ))}
        </div>

        <div className="childrenFormGrid">
          <input placeholder={form.profileType === "pet" ? cu(lang, "Evcil hayvan adı") : cu(lang, "Ad")} value={form.name} onChange={(e) => set("name", e.target.value)} />
          {form.profileType !== "pet" && <input placeholder={cu(lang, "Soyad")} value={form.surname} onChange={(e) => set("surname", e.target.value)} />}
          {form.profileType === "pet" && <SmartSelectInput placeholder={cu(lang, "Tür: kedi, köpek...")} value={form.petType} onChange={(v) => set("petType", v)} options={SMART_OPTIONS.petType} lang={lang} />}
          <div className="childrenDatePlaceholderWrap">
            <input
              type="date"
              value={form.birthDate}
              onChange={(e) => set("birthDate", e.target.value)}
              aria-label={cu(lang, "Doğum tarihi")}
            />
            {!form.birthDate && <span className="childrenDatePlaceholder">{cu(lang, "Doğum tarihi")}</span>}
          </div>
          <SmartSelectInput placeholder={cu(lang, "Cinsiyet")} value={form.gender} onChange={(v) => set("gender", v)} options={SMART_OPTIONS.gender} lang={lang} />
          {form.profileType !== "pet" ? (
            <SmartSelectInput placeholder={cu(lang, "Kan grubu")} value={form.bloodType} onChange={(v) => set("bloodType", v)} options={SMART_OPTIONS.bloodType} lang={lang} />
          ) : (
            <input placeholder={cu(lang, "Irk")} value={form.breed} onChange={(e) => set("breed", e.target.value)} />
          )}
          {form.profileType === "pet" && <input placeholder={cu(lang, "Çip numarası")} value={form.chipNo} onChange={(e) => set("chipNo", e.target.value)} />}
          {form.profileType === "pet" && <input placeholder={cu(lang, "Veteriner bilgisi")} value={form.vetInfo} onChange={(e) => set("vetInfo", e.target.value)} />}
          <input placeholder={form.profileType === "pet" ? cu(lang, "Acil veteriner / telefon") : cu(lang, "Acil kişi / telefon")} value={form.emergencyContact} onChange={(e) => set("emergencyContact", e.target.value)} />
          <textarea placeholder={cu(lang, "Alerjiler")} value={form.allergies} onChange={(e) => set("allergies", e.target.value)} />
          <textarea placeholder={t("children.placeholder.chronic")} value={form.chronicDiseases} onChange={(e) => set("chronicDiseases", e.target.value)} />
          <textarea placeholder={t("children.placeholder.medicines")} value={form.regularMedicines} onChange={(e) => set("regularMedicines", e.target.value)} />
          <textarea placeholder={cu(lang, "Notlar")} value={form.note} onChange={(e) => set("note", e.target.value)} />
        </div>
        {saving && <ChildrenSaveStatus label={cu(lang, "Profil güvenle oluşturuluyor")} lang={lang} />}
        <AsyncActionButton className="childrenPrimaryBtn childrenSaveBtn" onAction={save} externalBusy={saving} disabled={saving}>
          {cu(lang, "Kaydet")}
        </AsyncActionButton>
      </div>
    </div>
  );
}


function ChildrenInlineStyles() {
  return (
    <style>{`

      .childrenHeroPremium { position:relative; overflow:hidden; align-items:center; gap:14px; padding:16px; border:1px solid rgba(148,163,184,.18); border-radius:24px; background:linear-gradient(135deg, rgba(96,165,250,.14), rgba(244,114,182,.10) 52%, rgba(255,255,255,.06)); box-shadow:0 18px 42px rgba(15,23,42,.12); }
      .childrenHeroPremium:before { content:""; position:absolute; right:-54px; top:-68px; width:170px; height:170px; border-radius:999px; background:radial-gradient(circle, rgba(96,165,250,.26), transparent 68%); pointer-events:none; }
      .childrenHeroPremium:after { content:""; position:absolute; left:20px; bottom:-72px; width:150px; height:150px; border-radius:999px; background:radial-gradient(circle, rgba(244,114,182,.18), transparent 68%); pointer-events:none; }
      .childrenHeroCopy { position:relative; z-index:1; min-width:0; }
      .childrenHeroEyebrow { display:inline-flex; width:max-content; padding:5px 9px; border-radius:999px; background:rgba(255,255,255,.10); border:1px solid rgba(148,163,184,.18); color:var(--text-secondary); font-size:10.5px; font-weight:900; letter-spacing:.04em; }
      .childrenHeroCopy h2 { display:flex; align-items:center; gap:9px; margin:8px 0 5px; color:var(--text-primary); font-size:21px; line-height:1.14; letter-spacing:-.035em; font-weight:950; }
      .childrenHeroTitleIcon { display:grid; place-items:center; width:34px; height:34px; border-radius:14px; background:rgba(255,255,255,.14); border:1px solid rgba(148,163,184,.18); box-shadow:inset 0 1px 0 rgba(255,255,255,.18); font-size:17px; flex:0 0 auto; }
      .childrenHeroCopy p { margin:0; max-width:560px; color:var(--text-secondary); font-size:13px; line-height:1.42; font-weight:720; }
      .childrenHeroAddBtn { position:relative; z-index:1; flex:0 0 auto; min-width:104px; height:44px; padding:0 14px; border:0; border-radius:16px; display:inline-flex; align-items:center; justify-content:center; gap:8px; background:linear-gradient(135deg, #60a5fa, #a78bfa 54%, #f472b6); color:#fff; font-weight:950; box-shadow:0 14px 30px rgba(96,165,250,.28); cursor:pointer; }
      .childrenHeroAddBtn span { display:grid; place-items:center; width:23px; height:23px; border-radius:9px; background:rgba(255,255,255,.20); font-size:20px; line-height:1; margin-top:-1px; }
      .childrenHeroAddBtn b { font-size:12.5px; letter-spacing:-.01em; white-space:nowrap; }
      .childrenHeroAddBtn:active { transform:scale(.98); }
      body[data-tema="light"] .childrenHeroPremium { background:linear-gradient(135deg, rgba(239,246,255,.98), rgba(253,242,248,.94)); box-shadow:0 18px 38px rgba(15,23,42,.08); }
      @media (max-width:520px) { .childrenHeroPremium { align-items:flex-start; padding:14px; } .childrenHeroCopy h2 { font-size:18px; } .childrenHeroCopy p { font-size:12.4px; } .childrenHeroAddBtn { min-width:44px; width:44px; height:44px; padding:0; border-radius:15px; } .childrenHeroAddBtn b { display:none; } .childrenHeroAddBtn span { background:transparent; font-size:25px; } }

      .childrenDatePlaceholderWrap { position:relative; width:100%; min-width:0; }
      .childrenDatePlaceholderWrap input { width:100%; }
      .childrenDatePlaceholder { position:absolute; left:14px; top:50%; transform:translateY(-50%); color:var(--text-secondary); opacity:.62; pointer-events:none; font-size:13px; font-weight:600; }
      .childrenFilePicker { display:flex; align-items:center; gap:10px; width:100%; min-width:0; }
      .childrenFilePicker input { display:none; }
      .childrenFileButton { flex-shrink:0; padding:10px 13px; border-radius:12px; border:1px solid var(--border); background:rgba(255,255,255,0.06); color:var(--text-primary); font-size:12px; font-weight:800; cursor:pointer; }
      .childrenFileName { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:12px; color:var(--text-secondary); }
      .childrenMemoryPhotoBtn { width:100%; max-width:100%; overflow:hidden; border:0; padding:0; background:transparent; display:block; cursor:pointer; }
      .childrenMemoryPhotoBtn img { display:block; width:100%; max-width:100%; height:auto; max-height:220px; object-fit:contain; border-radius:14px; background:rgba(15,23,42,.16); }
      .childrenPhotoPreviewCard img { max-width:100%; max-height:72vh; object-fit:contain; border-radius:14px; }
      .childrenPhotoPreviewCard--premium { width:min(94vw,520px); max-height:88dvh; overflow:auto; padding:16px; border:1px solid rgba(129,140,248,.2); border-radius:24px; background:radial-gradient(circle at 10% 0%,rgba(99,102,241,.16),transparent 34%),var(--bg-elevated); box-shadow:0 28px 80px rgba(2,6,23,.46); }
      .childrenPhotoPreviewOpen { width:100%; min-height:220px; max-height:58dvh; display:grid; place-items:center; padding:0; overflow:hidden; border:0; border-radius:17px; background:rgba(15,23,42,.2); cursor:pointer; }
      .childrenPhotoPreviewOpen img { display:block; width:100%; height:100%; max-height:58dvh; object-fit:contain; border-radius:17px; }
      .childrenPhotoPreviewTitle { padding:10px 4px 4px; overflow:hidden; color:var(--text-primary); font-size:13px; font-weight:850; text-overflow:ellipsis; white-space:nowrap; }
      .childrenPhotoPreviewActions { grid-template-columns:repeat(3,minmax(0,1fr)) !important; margin-top:8px !important; }
      .childrenTimelineCard, .childrenTimelineItem, .childrenDetailBody { min-width:0; max-width:100%; overflow:hidden; }
      .childrenTimelineCard img { max-width:100%; }
      .childrenPremiumProfile .childrenAvatarBig { margin-bottom: 14px !important; }
      .childrenPremiumProfile .childrenProfileMain { padding-top: 2px; }
      .childrenPremiumProfile .childrenProfileTypePill { margin-top: 0; }
      .childrenGrowthPremium { position:relative; overflow:hidden; border:1px solid rgba(148,163,184,.20); border-radius:20px; padding:12px; margin:4px 0 10px; background:linear-gradient(145deg, rgba(255,255,255,.085), rgba(255,255,255,.03)); box-shadow:0 14px 32px rgba(15,23,42,.16); }
      .childrenGrowthPremium:before { content:""; position:absolute; inset:-42% -24% auto auto; width:160px; height:160px; border-radius:999px; background:radial-gradient(circle, rgba(96,165,250,.18), transparent 64%); pointer-events:none; }
      .childrenGrowthPremiumHead { position:relative; display:flex; align-items:flex-start; justify-content:space-between; gap:10px; margin-bottom:9px; }
      .childrenGrowthPremiumHead div { display:flex; align-items:center; gap:8px; min-width:0; }
      .childrenGrowthPremiumHead span { display:grid; place-items:center; width:28px; height:28px; border-radius:11px; background:rgba(96,165,250,.13); font-size:14px; }
      .childrenGrowthPremiumHead b { color:var(--text-primary); font-size:13.5px; font-weight:950; }
      .childrenGrowthPremiumHead em { color:var(--text-secondary); font-size:10px; font-style:normal; font-weight:850; text-align:right; line-height:1.3; }
      .childrenGrowthStats { position:relative; display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:7px; margin-bottom:9px; }
      .childrenGrowthStat { border:1px solid rgba(148,163,184,.16); border-radius:14px; padding:8px 9px; background:rgba(15,23,42,.10); min-width:0; }
      .childrenGrowthStat span { display:block; color:var(--text-secondary); font-size:10px; font-weight:850; margin-bottom:3px; }
      .childrenGrowthStat b { display:block; color:var(--text-primary); font-size:14.5px; font-weight:950; letter-spacing:-.02em; }
      .childrenGrowthStat small { display:block; margin-top:3px; color:var(--text-muted); font-size:9.5px; font-weight:800; }
      .childrenGrowthChartWrap { position:relative; border:1px solid rgba(148,163,184,.18); border-radius:17px; padding:9px 10px 8px; background:rgba(2,6,23,.10); }
      .childrenGrowthLanesHead { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:8px; }
      .childrenGrowthLanesHead b { color:var(--text-primary); font-size:11.5px; font-weight:950; }
      .childrenGrowthLanesHead span { color:var(--text-muted); font-size:10px; font-weight:850; }
      .childrenGrowthLane { display:grid; grid-template-columns:72px minmax(0,1fr); gap:8px; align-items:center; min-height:58px; padding:7px 0; border-top:1px solid rgba(148,163,184,.12); }
      .childrenGrowthLane:first-of-type { border-top:0; padding-top:0; }
      .childrenGrowthLaneInfo { min-width:0; }
      .childrenGrowthLaneInfo span { display:block; color:var(--text-secondary); font-size:10px; font-weight:900; }
      .childrenGrowthLaneInfo b { display:block; color:var(--text-primary); font-size:13px; font-weight:950; line-height:1.25; margin-top:2px; white-space:nowrap; }
      .childrenGrowthLaneInfo small { display:inline-flex; margin-top:3px; color:var(--text-muted); font-size:9.5px; font-weight:850; }
      .childrenGrowthSpark { display:block; width:100%; height:58px; overflow:visible; }
      .childrenGrowthSpark .baseline, .childrenGrowthSpark .guide { stroke:rgba(148,163,184,.20); stroke-width:1; stroke-dasharray:4 6; }
      .childrenGrowthSpark .area { opacity:.14; }
      .childrenGrowthSpark .line { stroke-width:3.2; stroke-linecap:round; stroke-linejoin:round; filter:drop-shadow(0 4px 7px rgba(15,23,42,.15)); }
      .childrenGrowthSpark .lastDot { stroke:rgba(255,255,255,.92); stroke-width:2.5; }
      .childrenGrowthLane.height .line, .childrenGrowthLane.height .lastDot { stroke:#60a5fa; fill:#60a5fa; }
      .childrenGrowthLane.height .area { fill:#60a5fa; }
      .childrenGrowthLane.weight .line, .childrenGrowthLane.weight .lastDot { stroke:#34d399; fill:#34d399; }
      .childrenGrowthLane.weight .area { fill:#34d399; }
      .childrenGrowthLane.head .line, .childrenGrowthLane.head .lastDot { stroke:#f59e0b; fill:#f59e0b; }
      .childrenGrowthLane.head .area { fill:#f59e0b; }
      .childrenGrowthDateRange { display:flex; justify-content:space-between; gap:8px; padding:2px 1px 0 80px; color:var(--text-muted); font-size:9.5px; font-weight:800; }
      .childrenTimeline.childrenTimelineScrollable { max-height:228px; overflow-y:auto; padding-right:4px; overscroll-behavior:contain; }
      .childrenTimeline.childrenTimelineScrollable::-webkit-scrollbar { width:4px; }
      .childrenTimeline.childrenTimelineScrollable::-webkit-scrollbar-thumb { background:rgba(148,163,184,.34); border-radius:999px; }
      .childrenTimelineCard.childrenPremiumRecordCard { position:relative; min-height:68px; padding:9px 34px 9px 12px; border-radius:15px; background:linear-gradient(145deg, rgba(255,255,255,.08), rgba(255,255,255,.032)); }
      .childrenTimelineCard.childrenPremiumRecordCard.hasMemoryPhoto, .childrenTimelineCard.hasMemoryPhoto.childrenPremiumRecordCard { min-height:118px; padding-top:11px; padding-bottom:11px; }
      .childrenTimelineCard.childrenPremiumRecordCard .childrenTimelineTop b { font-size:11.7px; }
      .childrenTimelineCard.childrenPremiumRecordCard .childrenTimelineTop small, .childrenTimelineCard.childrenPremiumRecordCard p, .childrenTimelineCard.childrenPremiumRecordCard em { font-size:10px; }
      .childrenHealthSectionCompact { display:flex; flex-direction:column; gap:13px; color:var(--text-primary); }
      .childrenHealthHero { position:relative; overflow:hidden; border:1px solid rgba(244,114,182,.20); border-radius:23px; padding:15px; background:linear-gradient(145deg, rgba(244,114,182,.14), rgba(96,165,250,.085) 52%, rgba(255,255,255,.052)); box-shadow:0 16px 36px rgba(15,23,42,.12); color:var(--text-primary); }
      .childrenHealthHero:before { content:""; position:absolute; inset:-35% -18% auto auto; width:152px; height:152px; border-radius:999px; background:radial-gradient(circle, rgba(244,114,182,.22), transparent 68%); pointer-events:none; }
      .childrenHealthHeroTop { position:relative; display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:13px; }
      .childrenHealthHeroTitle { display:flex; gap:11px; align-items:center; min-width:0; }
      .childrenHealthHeroIcon { position:relative; width:44px; height:44px; min-width:44px; min-height:44px; border-radius:15px; display:flex; align-items:center; justify-content:center; background:rgba(255,255,255,.18); border:1px solid rgba(255,255,255,.28); box-shadow:inset 0 1px 0 rgba(255,255,255,.28), 0 10px 22px rgba(15,23,42,.10); flex:0 0 44px; padding:0; overflow:hidden; }
      .childrenHealthHeroAvatar { width:100%; height:100%; display:block; border-radius:15px; overflow:hidden; line-height:0; }
      .childrenHealthHeroAvatar .memberAvatar, .childrenHealthHeroAvatar .avatarImgWrap { width:100% !important; height:100% !important; min-width:100% !important; min-height:100% !important; border-radius:15px !important; padding:0 !important; margin:0 !important; display:block !important; overflow:hidden !important; }
      .childrenHealthHeroAvatar img { width:100% !important; height:100% !important; min-width:100% !important; min-height:100% !important; border-radius:15px !important; object-fit:cover !important; object-position:center center !important; display:block !important; transform:scale(1.16) translateY(-2px); transform-origin:center top; }
      .childrenHealthHeroIconEmoji { display:none !important; }
      .childrenHealthHeroIcon::before { content:none; }
      .childrenHealthHeroTitle b { display:block; color:var(--text-primary); font-size:15.6px; font-weight:950; letter-spacing:-.02em; line-height:1.16; }
      .childrenHealthHeroTitle span { display:block; margin-top:4px; color:var(--text-secondary); font-size:12.7px; font-weight:760; line-height:1.38; }
      .childrenHealthTrustPill { display:inline-flex; align-items:center; gap:5px; flex:0 0 auto; padding:6px 9px; border-radius:999px; background:rgba(16,185,129,.13); border:1px solid rgba(16,185,129,.22); color:var(--success, #10b981); font-size:10.9px; font-weight:950; white-space:nowrap; }
      .childrenHealthHeroStats { position:relative; display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:8px; }
      .childrenHealthHeroStat { min-width:0; padding:9px 9px; border-radius:15px; background:rgba(255,255,255,.105); border:1px solid rgba(148,163,184,.16); }
      .childrenHealthHeroStat span { display:block; color:var(--text-secondary); font-size:10.2px; font-weight:900; margin-bottom:4px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      .childrenHealthHeroStat b { display:block; color:var(--text-primary); font-size:13px; font-weight:950; line-height:1.2; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      .childrenHealthChips { display:flex; gap:8px; overflow-x:auto; padding:2px 2px 5px; scrollbar-width:none; -webkit-overflow-scrolling:touch; }
      .childrenHealthChips::-webkit-scrollbar { display:none; }
      .childrenHealthChip { appearance:none; -webkit-appearance:none; flex:0 0 auto; border:1px solid rgba(148,163,184,.22); border-radius:999px; padding:8px 12px; background:rgba(255,255,255,.07); color:var(--text-secondary); font-size:12px; font-weight:900; cursor:pointer; touch-action:manipulation; }
      .childrenHealthChip:active { transform:scale(.98); }
      .childrenHealthChip.isActive { color:var(--text-primary); background:rgba(244,114,182,.16); border-color:rgba(244,114,182,.32); box-shadow:0 8px 18px rgba(244,114,182,.10); }
      .childrenHealthSectionCompact .childrenHealthInfo { padding:12px 14px; border-radius:17px; font-size:12.8px; line-height:1.48; color:var(--text-secondary); background:rgba(255,255,255,.06); border:1px solid rgba(148,163,184,.16); }
      .childrenHealthSectionCompact .childrenRecordForm { gap:10px; padding:13px; border-radius:18px; }
      .childrenHealthSectionCompact .childrenRecordForm input, .childrenHealthSectionCompact .childrenRecordForm textarea { font-size:13px; min-height:42px; color:var(--text-primary); }
      .childrenHealthSectionCompact .childrenAddSmallBtn { min-height:44px; padding:12px 15px; border-radius:16px; font-size:13.2px; box-shadow:0 10px 22px rgba(59,130,246,.12); }
      .childrenHealthSectionCompact .childrenHealthTimeline { display:flex !important; flex-direction:column !important; gap:10px !important; margin-top:2px; }
      .childrenHealthSectionCompact .childrenHealthTimeline.childrenTimelineScrollable { height:auto !important; max-height:356px !important; overflow-y:auto !important; overflow-x:hidden !important; padding-right:5px; }
      .childrenHealthSectionCompact .childrenHealthTimeline .childrenTimelineItem { display:block !important; min-height:auto !important; height:auto !important; overflow:visible !important; padding:0 !important; margin:0 !important; }
      .childrenHealthSectionCompact .childrenHealthTimeline .childrenTimelineDot { display:none !important; }
      .childrenHealthSectionCompact .childrenHealthTimeline .childrenTimelineCard { display:block !important; width:100% !important; min-height:104px !important; height:auto !important; max-height:none !important; overflow:visible !important; opacity:1 !important; transform:none !important; color:var(--text-primary) !important; border:1px solid rgba(148,163,184,.20); background:linear-gradient(145deg, rgba(255,255,255,.085), rgba(255,255,255,.035)); box-shadow:0 10px 24px rgba(15,23,42,.08); }
      .childrenHealthRecordCard { display:flex; flex-direction:column; gap:9px; }
      .childrenHealthRecordMain { display:flex; align-items:flex-start; justify-content:space-between; gap:9px; padding-right:24px; }
      .childrenHealthRecordTitle { min-width:0; }
      .childrenHealthRecordTitle b { display:block; color:var(--text-primary); font-size:14.1px; font-weight:940; line-height:1.28; letter-spacing:-.01em; white-space:normal; }
      .childrenHealthRecordTitle small { display:inline-flex; margin-top:5px; color:var(--text-muted); font-size:11.6px; font-weight:850; }
      .childrenHealthRecordType { display:none !important; }
      .childrenHealthRecordNote { margin:0; color:var(--text-secondary); font-size:12.8px; line-height:1.44; font-weight:720; white-space:normal; }
      .childrenHealthRecordMetaGrid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:7px; }
      .childrenHealthRecordMeta { min-width:0; border-radius:12px; padding:7px 8px; background:rgba(148,163,184,.08); border:1px solid rgba(148,163,184,.13); }
      .childrenHealthRecordMeta span { display:block; color:var(--text-muted); font-size:9.8px; font-weight:900; margin-bottom:2px; }
      .childrenHealthRecordMeta b { display:block; color:var(--text-primary); font-size:11.8px; font-weight:900; line-height:1.25; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
      .childrenDocumentHint { display:inline-flex; align-items:center; gap:6px; margin-top:2px; padding:6px 10px; border-radius:999px; background:rgba(96,165,250,.13); color:var(--text-primary); border:1px solid rgba(96,165,250,.22); font-size:11.2px; font-weight:900; }
      .childrenHealthPreviewThumb { width:100%; min-height:62px; border:1px solid rgba(148,163,184,.16); border-radius:14px; padding:7px; background:rgba(255,255,255,.06); display:flex; align-items:center; gap:10px; cursor:pointer; color:var(--text-primary); text-align:left; }
      .childrenHealthPreviewThumb img { width:54px; height:54px; object-fit:contain; border-radius:11px; flex:0 0 auto; background:rgba(15,23,42,.16); }
      .childrenHealthPreviewThumb small { display:block; color:var(--text-secondary); font-size:11.5px; font-weight:900; }
      .childrenTimelineCard.childrenHasDocument { cursor:pointer; border-color:rgba(96,165,250,.28); }
      .childrenTimelineCard.childrenHasDocument:hover { transform:translateY(-1px); background:linear-gradient(145deg, rgba(96,165,250,.11), rgba(255,255,255,.04)); }
      .childrenDocumentModal .belgeOnizleme { width:100%; max-height:min(34dvh,230px); object-fit:contain; border-radius:16px; background:rgba(15,23,42,.18); }
      .childrenDocumentModal .belgeDosyaKart { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; min-height:135px; max-height:210px; padding:16px; border-radius:16px; border:1px solid var(--border); background:rgba(255,255,255,.06); text-align:center; }
      .childrenDocumentModal .belgeDosyaAd { color:var(--text-primary); font-weight:850; word-break:break-word; }
      .childrenDocumentModal .belgeDosyaBoyut { color:var(--text-muted); font-size:12px; }
      .childrenDocumentModal .belgeMetaList { display:grid; gap:8px; margin-top:12px; }
      .childrenDocumentModal .belgeMetaRow { display:flex; justify-content:space-between; gap:12px; padding:9px 10px; border-radius:12px; background:rgba(148,163,184,.10); }
      .childrenDocumentModal .belgeMetaLbl { color:var(--text-muted); font-size:11px; font-weight:800; }
      .childrenDocumentModal .belgeMetaVal { color:var(--text-primary); font-size:12px; font-weight:850; text-align:right; }
      .childrenDocumentModal .belgeAksiyonlar { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:8px; margin-top:13px; }
      .childrenDocumentModal .belgeAksiyonBtn { border:1px solid var(--border); border-radius:13px; padding:10px 8px; text-decoration:none; font-size:12px; font-weight:900; text-align:center; color:var(--text-primary); background:rgba(255,255,255,.07); }
      .childrenHealthPreviewThumb { width:100%; margin:7px 0 6px; border:1px solid rgba(148,163,184,.18); border-radius:14px; padding:7px; background:rgba(255,255,255,.055); display:grid; grid-template-columns:52px minmax(0,1fr); align-items:center; gap:9px; text-align:left; color:var(--text-primary); box-shadow:none; }
      .childrenHealthPreviewThumb img { width:52px; height:52px; object-fit:contain; border-radius:11px; display:block; background:rgba(15,23,42,.18); }
      .childrenHealthPreviewThumb small { color:#93c5fd; font-size:11.2px; font-weight:900; }
      .childrenHealthPreviewThumb:active { transform:scale(.99); }
      .childrenDocumentModal .belgeAksiyonBtn.primary { background:rgba(59,130,246,.16); border-color:rgba(59,130,246,.26); color:#93c5fd; }
      .childrenDocumentModal .belgeAksiyonBtn.secondary { background:rgba(148,163,184,.12); }
      .childrenDocumentModal .belgeAksiyonBtn.danger { background:rgba(248,113,113,.12); border-color:rgba(248,113,113,.24); color:#fb7185; }
      .childrenTimelineCard .childrenRecordDeleteMini { position:absolute; top:8px; right:8px; width:24px; height:24px; min-width:24px; border:1px solid rgba(248,113,113,.24); border-radius:999px; padding:0; display:grid; place-items:center; color:#fb7185; background:rgba(248,113,113,.10); font-size:12px; line-height:1; font-weight:950; box-shadow:none; }
      .childrenTimelineCard .childrenRecordDeleteMini:hover { transform:translateY(-1px); background:rgba(248,113,113,.16); }
      body[data-tema="light"] .childrenGrowthPremium { background:linear-gradient(145deg, rgba(255,255,255,.98), rgba(248,250,252,.92)); box-shadow:0 14px 30px rgba(15,23,42,.07); }
      body[data-tema="light"] .childrenGrowthStat, body[data-tema="light"] .childrenGrowthChartWrap { background:rgba(248,250,252,.88); }
      @media (max-width:520px) { .childrenGrowthStats { grid-template-columns:repeat(3,minmax(0,1fr)); gap:6px; } .childrenGrowthStat { padding:7px 7px; } .childrenGrowthStat b { font-size:13px; } .childrenGrowthLane { grid-template-columns:66px minmax(0,1fr); gap:7px; } .childrenGrowthDateRange { padding-left:73px; } .childrenTimeline.childrenTimelineScrollable { max-height:218px; } .childrenHealthSectionCompact .childrenHealthHero { padding:12px; border-radius:20px; } .childrenHealthHeroStats { grid-template-columns:repeat(2,minmax(0,1fr)); } .childrenHealthSectionCompact .childrenHealthTimeline.childrenTimelineScrollable { max-height:340px !important; } .childrenDocumentModal .belgeAksiyonlar { grid-template-columns:repeat(3,minmax(0,1fr)); } }
    `}</style>
  );
}

function FilePicker({ file, onChange, accept, lang = "tr" }) {
  const inputId = useMemo(() => `children-file-${Math.random().toString(36).slice(2)}`, []);
  return (
    <div className="childrenFilePicker">
      <input id={inputId} type="file" accept={accept} onChange={(e) => onChange(e.target.files?.[0] || null)} />
      <label className="childrenFileButton" htmlFor={inputId}>📎 {cu(lang, "Dosya seç")}</label>
      <span className="childrenFileName">{file?.name || cu(lang, "Dosya seçilmedi")}</span>
    </div>
  );
}

function RecordForm({ type, profileType, onSave, onClose, belgeYukle, child, user, uploading = false, uploadProgress = 0 }) {
  const { t, lang } = useLanguage();
  const recordValidation = useFormValidation(`child-record-${type}`);
  const [form, setForm] = useState({ date: today() });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const saveInFlightRef = useRef(false);
  const uploadedFileRef = useRef({ file: null, info: null });
  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    recordValidation.clearError(k);
    if (["height", "weight", "head", "size", "milestone"].includes(k)) recordValidation.clearError("growthValue");
  };

  const requiredRecordErrors = () => {
    if (type === "growth") return [![form.height, form.weight, form.head, form.size, form.milestone].some((value) => String(value || "").trim()) && "growthValue"];
    if (["health", "education", "routine", "diaper", "care", "memory"].includes(type)) return [!form.title?.trim() && "title"];
    if (type === "nutrition") return [!form.meal?.trim() && "meal", !form.food?.trim() && "food"];
    return [];
  };

  const recordRequiredLabels = {
    growthValue: cu(lang, "En az bir gelişim ölçümü"),
    title: type === "memory" ? cu(lang, "Anı başlığı") : cu(lang, "Kayıt türü / başlık"),
    meal: cu(lang, "Öğün"),
    food: profileType === "pet" ? cu(lang, "Mama / besin") : cu(lang, "Ne yedi?"),
  };

  const save = async () => {
    if (saveInFlightRef.current) return;
    if (!recordValidation.validate(requiredRecordErrors())) return;
    saveInFlightRef.current = true;
    setSaving(true);
    setSaveProgress(file ? 4 : 18);
    let fileInfo = null;
    try {
      if (file && belgeYukle) {
        // Belge yüklenip kayıt isteği başarısız olursa aynı formdaki tekrar deneme
        // dosyayı ikinci kez yüklemez; hazır yükleme sonucu güvenle yeniden kullanılır.
        if (uploadedFileRef.current.file === file && uploadedFileRef.current.info) {
          fileInfo = uploadedFileRef.current.info;
        } else {
          const uploadedDoc = await belgeYukle({
            dosya: file,
            ad: form.title || form.name || file.name,
            aciklama: form.note || "",
            kategori: type === "memory" ? "👶 Çocuk Anısı" : profileType === "pet" ? "🐾 Evcil Hayvan" : "👶 Çocuk Takibi",
            gecerlilikTarihi: null,
            tip: "kisisel",
            sahipUid: child.memberUid || child.id || user?.uid,
            sahipAd: child.displayName || child.name || "Profil",
            sahipAvatar: getDefaultChildAvatar(child),
            sahipProfileType: profileType,
            gorebilecekler: [],
            storageType: "children",
          });
          fileInfo = {
            fileName: file.name,
            fileType: uploadedDoc?.fileType || file.name.split(".").pop()?.toLowerCase() || "",
            fileUrl: uploadedDoc?.url || uploadedDoc?.fileUrl || uploadedDoc?.dosyaUrl || "",
            documentId: uploadedDoc?.id || null,
          };
          uploadedFileRef.current = { file, info: fileInfo };
        }
        setSaveProgress(92);
      } else {
        setSaveProgress(62);
      }
      await onSave({ ...form, ...fileInfo });
      setSaveProgress(100);
    } catch (error) {
      console.error("[ChildrenPage] Kayıt kaydedilemedi:", error?.code || error?.message);
      throw error;
    } finally {
      saveInFlightRef.current = false;
      setSaving(false);
      setSaveProgress(0);
    }
  };

  const selectFile = (nextFile) => {
    uploadedFileRef.current = { file: null, info: null };
    setFile(nextFile);
  };

  const visibleProgress = file
    ? (uploading && Number(uploadProgress) > 0 ? Number(uploadProgress) : saveProgress)
    : null;

  const commonDate = <input type="date" value={form.date || ""} onChange={(e) => set("date", e.target.value)} />;

  return (
    <div className="childrenRecordForm" aria-busy={saving}>
      {type === "growth" && <>
        {commonDate}
        <div className={recordValidation.fieldClass("growthValue", "childrenGrowthRequiredGroup")} data-required-field={`child-record-${type}-growthValue`} aria-invalid={recordValidation.hasError("growthValue")}>
          <div className="childrenFormRow"><input placeholder={cu(lang, "Boy cm")} value={form.height || ""} onChange={(e) => set("height", e.target.value)} /><input placeholder={cu(lang, "Kilo kg")} value={form.weight || ""} onChange={(e) => set("weight", e.target.value)} /></div>
          <div className="childrenFormRow"><input placeholder={cu(lang, "Baş çevresi")} value={form.head || ""} onChange={(e) => set("head", e.target.value)} /><input placeholder={cu(lang, "Ayakkabı no / beden")} value={form.size || ""} onChange={(e) => set("size", e.target.value)} /></div>
          <SmartSelectInput placeholder={t("children.placeholder.milestone")} value={form.milestone || ""} onChange={(v) => set("milestone", v)} options={SMART_OPTIONS.development} lang={lang} />
        </div>
      </>}

      {type === "health" && <>
        {commonDate}
        <div data-required-field={`child-record-${type}-title`}>
          <SmartSelectInput className={recordValidation.fieldClass("title")} placeholder={profileType === "pet" ? cu(lang, "Aşı, parazit, veteriner...") : cu(lang, "Ateş, ilaç, aşı, doktor...")} value={form.title || ""} onChange={(v) => set("title", v)} options={profileType === "pet" ? SMART_OPTIONS.petHealth : SMART_OPTIONS.healthType} lang={lang} />
        </div>
        <div className="childrenFormRow"><input placeholder={cu(lang, "Değer / doz / ölçüm")} value={form.value || ""} onChange={(e) => set("value", e.target.value)} /><input placeholder={profileType === "pet" ? cu(lang, "Veteriner") : cu(lang, "Doktor / branş")} value={form.doctor || ""} onChange={(e) => set("doctor", e.target.value)} /></div>
        <FilePicker file={file} onChange={selectFile} lang={lang} />
      </>}

      {type === "education" && <>
        <div data-required-field={`child-record-${type}-title`}><SmartSelectInput className={recordValidation.fieldClass("title")} placeholder={t("children.placeholder.education")} value={form.title || ""} onChange={(v) => set("title", v)} options={SMART_OPTIONS.education} lang={lang} /></div>
        <div className="childrenFormRow"><input placeholder={cu(lang, "Okul adı")} value={form.school || ""} onChange={(e) => set("school", e.target.value)} /><input placeholder={cu(lang, "Sınıf")} value={form.className || ""} onChange={(e) => set("className", e.target.value)} /></div>
        <div className="childrenFormRow"><input placeholder={cu(lang, "Öğretmen")} value={form.teacher || ""} onChange={(e) => set("teacher", e.target.value)} /><input placeholder={cu(lang, "Tarih")} type="date" value={form.date || ""} onChange={(e) => set("date", e.target.value)} /></div>
        <input placeholder={cu(lang, "Servis / etkinlik / detay")} value={form.serviceInfo || ""} onChange={(e) => set("serviceInfo", e.target.value)} />
        <FilePicker file={file} onChange={selectFile} lang={lang} />
      </>}

      {type === "routine" && <>
        {commonDate}
        <div data-required-field={`child-record-${type}-title`}><SmartSelectInput className={recordValidation.fieldClass("title")} placeholder={profileType === "pet" ? cu(lang, "Aktivite / davranış") : cu(lang, "Rutin türü")} value={form.title || ""} onChange={(v) => set("title", v)} options={profileType === "pet" ? SMART_OPTIONS.petActivity : SMART_OPTIONS.routine} lang={lang} /></div>
        <div className="childrenFormRow"><input placeholder={cu(lang, "Başlangıç / saat")} value={form.startTime || ""} onChange={(e) => set("startTime", e.target.value)} /><input placeholder={cu(lang, "Süre / miktar")} value={form.duration || ""} onChange={(e) => set("duration", e.target.value)} /></div>
        <SmartSelectInput placeholder={cu(lang, "Ruh hali / kalite")} value={form.mood || ""} onChange={(v) => set("mood", v)} options={profileType === "baby" ? SMART_OPTIONS.sleepQuality : SMART_OPTIONS.mood} lang={lang} />
      </>}

      {type === "nutrition" && <>
        {commonDate}
        <div data-required-field={`child-record-${type}-meal`}><SmartSelectInput className={recordValidation.fieldClass("meal")} placeholder={cu(lang, "Öğün")} value={form.meal || ""} onChange={(v) => set("meal", v)} options={profileType === "baby" ? SMART_OPTIONS.babyMeal : SMART_OPTIONS.meal} lang={lang} /></div>
        <input className={recordValidation.fieldClass("food")} data-required-field={`child-record-${type}-food`} aria-invalid={recordValidation.hasError("food")} placeholder={profileType === "pet" ? cu(lang, "Mama / besin") : cu(lang, "Ne yedi?")} value={form.food || ""} onChange={(e) => set("food", e.target.value)} />
        <SmartSelectInput placeholder={cu(lang, "Miktar")} value={form.amount || ""} onChange={(v) => set("amount", v)} options={SMART_OPTIONS.amount} lang={lang} />
        <input placeholder={cu(lang, "Tepki / alerji / iştah")} value={form.reaction || ""} onChange={(e) => set("reaction", e.target.value)} />
      </>}

      {type === "diaper" && <>
        {commonDate}
        <div data-required-field={`child-record-${type}-title`}><SmartSelectInput className={recordValidation.fieldClass("title")} placeholder={cu(lang, "Bez durumu")} value={form.title || ""} onChange={(v) => set("title", v)} options={SMART_OPTIONS.diaperType} lang={lang} /></div>
        <div className="childrenFormRow"><input placeholder={cu(lang, "Saat")} value={form.time || ""} onChange={(e) => set("time", e.target.value)} /><input placeholder={cu(lang, "Renk / durum")} value={form.value || ""} onChange={(e) => set("value", e.target.value)} /></div>
      </>}

      {type === "care" && <>
        {commonDate}
        <div data-required-field={`child-record-${type}-title`}><SmartSelectInput className={recordValidation.fieldClass("title")} placeholder={cu(lang, "Bakım türü")} value={form.title || ""} onChange={(v) => set("title", v)} options={SMART_OPTIONS.petCare} lang={lang} /></div>
        <input placeholder={cu(lang, "Detay / ürün / uygulama")} value={form.value || ""} onChange={(e) => set("value", e.target.value)} />
      </>}

      {type === "memory" && <>
        <input className={recordValidation.fieldClass("title")} data-required-field={`child-record-${type}-title`} aria-invalid={recordValidation.hasError("title")} placeholder={cu(lang, "Anı başlığı")} value={form.title || ""} onChange={(e) => set("title", e.target.value)} />
        {commonDate}
        <SmartSelectInput placeholder={cu(lang, "Kategori")} value={form.category || ""} onChange={(v) => set("category", v)} options={SMART_OPTIONS.memory} lang={lang} />
        <FilePicker file={file} onChange={selectFile} accept="image/*" lang={lang} />
      </>}

      <textarea placeholder={cu(lang, "Not")} value={form.note || ""} onChange={(e) => set("note", e.target.value)} />
      {saving && (
        <ChildrenSaveStatus
          label={file ? cu(lang, type === "memory" ? "Fotoğraf yükleniyor" : "Dosya yükleniyor") : cu(lang, "Kayıt güvenle oluşturuluyor")}
          progress={visibleProgress}
          lang={lang}
        />
      )}
      <FormValidationNotice errors={recordValidation.errors} labels={recordRequiredLabels} />
      <div className="childrenFormActions">
        <button onClick={onClose} disabled={saving}>{cu(lang, "Vazgeç")}</button>
        <AsyncActionButton onAction={save} externalBusy={saving} disabled={saving}>{cu(lang, "Kaydet")}</AsyncActionButton>
      </div>
    </div>
  );
}

function DetailSection({ title, icon, children, open, onClick, side = "left" }) {
  const asset = CHILD_SECTION_ASSETS[icon];
  return (
    <div className={`childrenDetailCard ${open ? "open" : ""} ${side === "right" ? "rightCard" : "leftCard"}`}>
      <button className="childrenDetailCardTop" onClick={onClick}>
        {asset ? (
          <span className={`appFeatureIcon childrenSectionAsset childrenSectionAsset--${icon}`} aria-hidden="true">
            <img src={asset} alt="" draggable="false" />
          </span>
        ) : (
          <AppFeatureIcon type={icon} />
        )}
        <b>{title}</b>
        <em>{open ? "−" : "›"}</em>
      </button>
      {open && <div className="childrenDetailBody">{children}</div>}
    </div>
  );
}

function RecordList({ records, empty, onDelete, render, lang = "tr", maxVisible = null, premium = false, compactHealth = false, listClassName = "", onDocumentDelete, user, yoneticiMi, canManage = false }) {
  const { t } = useLanguage();
  const [preview, setPreview] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [secureViewerItem, setSecureViewerItem] = useState(null);

  useEffect(() => { setPreviewError(false); }, [selectedDoc?.id, selectedDoc?.dosyaUrl]);

  const handleDownload = async (doc) => {
    if (!doc || downloading) return;
    setDownloading(true);
    try {
      const blob = await fetchBelgeBlob(doc.dosyaUrl || doc.fileUrl);
      const finalMime = blob.type || (String(doc.dosyaTipi || doc.fileType || "").includes("/") ? (doc.dosyaTipi || doc.fileType) : "application/octet-stream");
      const finalName = safeDosyaAdi(doc.dosyaAdi || doc.fileName || doc.ad, doc.dosyaTipi || doc.fileType || finalMime, doc.ad || "saglik-belgesi");
      const nativeBridge = window?.webkit?.messageHandlers?.aileNativeBridge;
      if (nativeBridge) {
        const base64 = await blobToBase64(blob);
        nativeBridge.postMessage({ action: "shareFile", base64, mimeType: finalMime, fileName: finalName });
        return;
      }
      if (navigator?.canShare && window.File) {
        const file = new File([blob], finalName, { type: finalMime });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: finalName });
          return;
        }
      }
      if (isAndroidPlatform()) {
        const shared = await androidDosyaPaylas(blob, finalName, finalName);
        if (shared) return;
      }
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = finalName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    } catch (_) {
      await appAlert(lang === "en" ? "File could not be downloaded." : "Dosya indirilemedi.");
    } finally {
      setDownloading(false);
    }
  };

  const handleDocumentDelete = async (doc) => {
    if (!doc || !onDocumentDelete) return;
    const ok = await appConfirm(lang === "en" ? "Delete this health document?" : "Bu sağlık belgesi silinsin mi?");
    if (!ok) return;
    await onDocumentDelete({ belgeId: doc.id, belge: doc });
    setSelectedDoc(null);
  };

  return (
    <>
      <div className={`childrenTimeline ${compactHealth ? "childrenHealthTimeline" : ""} ${listClassName || ""} ${maxVisible && records.length > maxVisible ? "childrenTimelineScrollable" : ""}`}>
        {records.length === 0 && <div className="childrenEmptyMini">{empty || cu(lang, "Henüz kayıt yok.")}</div>}
        {records.map((r) => {
          const data = render(r);
          const doc = data.document || null;
          const documentUrl = doc?.dosyaUrl || data.fileUrl || r.fileUrl || "";
          const documentName = doc?.dosyaAdi || data.fileName || r.fileName || data.title || t("children.memoryPhoto");
          const documentType = doc?.dosyaTipi || data.fileType || r.fileType || "";
          const imageUrl = data.imageUrl || r.fileUrl || "";
          const imagePreviewUrl = data.previewUrl || r.previewUrl || doc?.previewUrl || imageUrl;
          const imageName = data.fileName || r.fileName || data.title || t("children.memoryPhoto");
          const showImage = imageUrl && isImageFile(imageName, data.fileType || r.fileType);
          const hasDocument = !!documentUrl && !showImage;
          const openDocument = () => {
            if (!hasDocument) return;
            setSelectedDoc(doc || {
              id: r.documentId || r.id,
              ad: data.title || documentName,
              dosyaAdi: documentName,
              dosyaTipi: documentType,
              dosyaUrl: documentUrl,
              previewUrl: data.previewUrl || r.previewUrl || doc?.previewUrl || null,
              dosyaBoyutu: data.fileSize || r.fileSize || null,
              aciklama: r.note || data.text || "",
              sahipAd: data.ownerName || "",
              yukleyenUid: data.uploaderUid || r.createdBy || null,
            });
          };
          return (
            <div className="childrenTimelineItem" key={r.id || r.firebaseId || r.documentId || `${r.date || "no-date"}-${r.title || r.name || r.fileName || Math.random()}`}>
              <div className="childrenTimelineDot" />
              <div
                className={`childrenTimelineCard ${showImage ? "hasMemoryPhoto" : ""} ${premium ? "childrenPremiumRecordCard" : ""} ${hasDocument ? "childrenHasDocument" : ""}`}
                onClick={openDocument}
                role={hasDocument ? "button" : undefined}
                tabIndex={hasDocument ? 0 : undefined}
                onKeyDown={(e) => { if (hasDocument && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); openDocument(); } }}
              >
                {compactHealth ? (
                  <div className="childrenHealthRecordCard">
                    <div className="childrenHealthRecordMain">
                      <div className="childrenHealthRecordTitle">
                        <b>{cu(lang, data.title)}</b>
                        <small>{formatDate(r.date || r.createdAt || r.tarih, lang)}</small>
                      </div>
                    </div>
                    {data.text && <p className="childrenHealthRecordNote">{cu(lang, data.text)}</p>}
                    {Array.isArray(data.metaRows) && data.metaRows.length > 0 && (
                      <div className="childrenHealthRecordMetaGrid">
                        {data.metaRows.slice(0, 4).map((m, idx) => (
                          <div className="childrenHealthRecordMeta" key={`${m.label || idx}-${idx}`}>
                            <span>{cu(lang, m.label)}</span><b>{cu(lang, m.value)}</b>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : <div className="childrenTimelineTop"><b>{cu(lang, data.title)}</b><small>{formatDate(r.date, lang)}</small></div>}
                {showImage && (
                  compactHealth ? (
                    <button type="button" className="childrenHealthPreviewThumb" aria-label={lang === "en" ? "Open attachment preview" : "Ek önizlemeyi aç"} onClick={(e) => {
                      e.stopPropagation();
                      setSelectedDoc(doc || {
                        id: r.documentId || r.id,
                        ad: data.title || imageName,
                        dosyaAdi: imageName,
                        dosyaTipi: data.fileType || r.fileType || imageName,
                        dosyaUrl: imageUrl,
                        previewUrl: imagePreviewUrl,
                        dosyaBoyutu: data.fileSize || r.fileSize || null,
                        aciklama: r.note || data.text || "",
                        sahipAd: data.ownerName || "",
                        yukleyenUid: data.uploaderUid || r.createdBy || null,
                      });
                    }}>
                      <RenewableFileImage src={imagePreviewUrl} alt={data.title || imageName} loading="lazy" decoding="async" />
                      <small>📎 {lang === "en" ? "Tap to preview" : "Önizleme"}</small>
                    </button>
                  ) : (
                    <button type="button" className="childrenMemoryPhotoBtn" onClick={(e) => { e.stopPropagation(); setPreview({ url: imageUrl, title: data.title || imageName, id: r.id, fileName: imageName, fileType: data.fileType || r.fileType || "image/jpeg" }); }}>
                      <RenewableFileImage src={imagePreviewUrl} alt={data.title || imageName} loading="lazy" decoding="async" />
                      <span>{cu(lang, "Fotoğrafı aç")}</span>
                    </button>
                  )
                )}
                {!compactHealth && <p>{data.text}</p>}
                {hasDocument && <span className="childrenDocumentHint">📎 {lang === "en" ? "Tap to preview document" : "Belge önizle"}</span>}
                {!compactHealth && r.note && <em>{cu(lang, r.note)}</em>}
                {onDelete && <button className="childrenRecordDeleteMini" aria-label={cu(lang, "Sil")} title={cu(lang, "Sil")} onClick={(e) => { e.stopPropagation(); onDelete(r.id); }}>×</button>}
              </div>
            </div>
          );
        })}
      </div>
      {preview && (
        <div className="childrenPhotoPreviewOverlay" onClick={() => setPreview(null)}>
          <div className="childrenPhotoPreviewCard childrenPhotoPreviewCard--premium" onClick={(e) => e.stopPropagation()}>
            <button className="childrenPhotoPreviewClose" onClick={() => setPreview(null)}>×</button>
            <button type="button" className="childrenPhotoPreviewOpen" onClick={() => setSecureViewerItem({ url: preview.url, fileName: preview.fileName || preview.title, mimeType: preview.fileType, title: preview.title })}>
              <RenewableFileImage src={preview.url} alt={preview.title} />
            </button>
            <div className="childrenPhotoPreviewTitle">{preview.title}</div>
            <div className="belgeAksiyonlar childrenPhotoPreviewActions">
              <button type="button" className="belgeAksiyonBtn primary" onClick={() => setSecureViewerItem({ url: preview.url, fileName: preview.fileName || preview.title, mimeType: preview.fileType, title: preview.title })}><MediaActionIcon type="view" /> {lang === "en" ? "View" : "Görüntüle"}</button>
              <button type="button" className="belgeAksiyonBtn secondary" disabled={downloading} onClick={() => handleDownload({ dosyaUrl: preview.url, dosyaAdi: preview.fileName || preview.title, dosyaTipi: preview.fileType })}>{downloading ? <MediaActionLoading /> : <MediaActionIcon type="download" />} {lang === "en" ? "Download" : "İndir"}</button>
              {preview.id && onDelete && <button type="button" className="belgeAksiyonBtn danger" onClick={async () => { if (await onDelete(preview.id)) setPreview(null); }}><MediaActionIcon type="delete" /> {cu(lang, "Sil")}</button>}
            </div>
          </div>
        </div>
      )}
      {selectedDoc && (
        <div className="modalOverlay" onClick={() => setSelectedDoc(null)}>
          <div className="darkModal belgeDetayModal healthDocumentModal childrenDocumentModal" onClick={(e) => e.stopPropagation()}>
            <div className="darkModalHeader">
              <span className="darkModalTitle">{dosyaIkonu(selectedDoc.dosyaTipi || selectedDoc.dosyaAdi)} {selectedDoc.ad || selectedDoc.dosyaAdi}</span>
              <button className="ayarKapatBtn" onClick={() => setSelectedDoc(null)}>×</button>
            </div>
            <button type="button" className="mediaDetailPreviewTrigger" onClick={() => setSecureViewerItem({ url: selectedDoc.dosyaUrl, fileName: selectedDoc.dosyaAdi || selectedDoc.ad, mimeType: selectedDoc.dosyaTipi, title: selectedDoc.ad || selectedDoc.dosyaAdi })} aria-label={lang === "en" ? "Open document preview" : "Belge önizlemesini aç"}>
            {["jpg", "jpeg", "png", "gif", "webp"].includes(normalizeDosyaTipi(selectedDoc.dosyaTipi || selectedDoc.dosyaAdi)) && !previewError ? (
              <RenewableFileImage src={selectedDoc.previewUrl || selectedDoc.dosyaUrl} alt={selectedDoc.ad || selectedDoc.dosyaAdi || ""} className="belgeOnizleme" onError={() => setPreviewError(true)} decoding="async" />
            ) : (
              <div className="belgeDosyaKart">
                <span style={{ fontSize: "42px" }}>{dosyaIkonu(selectedDoc.dosyaTipi || selectedDoc.dosyaAdi)}</span>
                <span className="belgeDosyaAd">{previewError ? (lang === "en" ? "Preview could not be loaded." : "Önizleme yüklenemedi.") : selectedDoc.dosyaAdi}</span>
                <span className="belgeDosyaBoyut">{boyutFormatla(selectedDoc.dosyaBoyutu)}</span>
              </div>
            )}
            </button>
            <div className="belgeMetaList">
              <div className="belgeMetaRow"><span className="belgeMetaLbl">{lang === "en" ? "Category" : "Kategori"}</span><span className="belgeMetaVal">🏥 {lang === "en" ? "Health" : "Sağlık"}</span></div>
              {selectedDoc.sahipAd && <div className="belgeMetaRow"><span className="belgeMetaLbl">{lang === "en" ? "Owner" : "Sahip"}</span><span className="belgeMetaVal">{selectedDoc.sahipAd}</span></div>}
              {selectedDoc.aciklama && <div className="belgeMetaRow"><span className="belgeMetaLbl">📝 {lang === "en" ? "Description" : "Açıklama"}</span><span className="belgeMetaVal">{selectedDoc.aciklama}</span></div>}
            </div>
            <div className="belgeAksiyonlar">
              <button type="button" className="belgeAksiyonBtn primary" onClick={() => setSecureViewerItem({
                url: selectedDoc.dosyaUrl,
                fileName: selectedDoc.dosyaAdi || selectedDoc.ad,
                mimeType: selectedDoc.dosyaTipi,
                title: selectedDoc.ad || selectedDoc.dosyaAdi,
              })}><MediaActionIcon type="view" /> {lang === "en" ? "View" : "Görüntüle"}</button>
              <button className="belgeAksiyonBtn secondary" onClick={() => handleDownload(selectedDoc)} disabled={downloading}>{downloading ? <MediaActionLoading /> : <MediaActionIcon type="download" />} {lang === "en" ? "Download" : "İndir"}</button>
              {(canManage || yoneticiMi || selectedDoc.yukleyenUid === user?.uid || !selectedDoc.yukleyenUid) && onDocumentDelete && (
                <button className="belgeAksiyonBtn danger" onClick={() => handleDocumentDelete(selectedDoc)}><MediaActionIcon type="delete" /> {cu(lang, "Sil")}</button>
              )}
            </div>
          </div>
        </div>
      )}
      <SecureMediaViewer open={!!secureViewerItem} item={secureViewerItem} onClose={() => setSecureViewerItem(null)} lang={lang} />
    </>
  );
}


export default function ChildrenPage({ familyId, uyeler = [], user, yoneticiMi, permissions, belgeler = [], belgeYukle, belgeSil, yukleniyor, yuklemePct, onChildrenDetailStateChange, onDetailBackRequest, isPremium = false, premiumConfig = {}, onPremiumLock, onPremiumLimitHit, onInternalForwardTransition }) {
  const { t, lang } = useLanguage();
  useChildrenKeyboardViewport();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const limits = premiumConfig?.limits || {};
  const lockedFeatures = premiumConfig?.lockedFeatures || {};

  const [manualChildren, setManualChildren] = useState([]);
  const [healthRecords, setHealthRecords] = useState([]);
  const [childRecords, setChildRecords] = useState([]);
  const [childrenBundleLoading, setChildrenBundleLoading] = useState(false);
  const [childrenBundleLoaded, setChildrenBundleLoaded] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [showAddChild, setShowAddChild] = useState(false);
  const childrenGridRef = useRef(null);
  const [childrenGridTasiyor, setChildrenGridTasiyor] = useState(false);
  const [openSection, setOpenSection] = useState("");
  const [addingType, setAddingType] = useState(null);
  const [selectedHealthFilter, setSelectedHealthFilter] = useState("all");
  const [selectedEducationFilter, setSelectedEducationFilter] = useState("all");
  const [selectedRoutineFilter, setSelectedRoutineFilter] = useState("all");
  const [selectedNutritionFilter, setSelectedNutritionFilter] = useState("all");
  const [selectedMemoryFilter, setSelectedMemoryFilter] = useState("all");
  const [showProfileDetails, setShowProfileDetails] = useState(false);
  // silOnayChild kaldırıldı — appConfirm kullanılıyor
  const [avatarSeciciAcik, setAvatarSeciciAcik] = useState(false);
  const [childAvatarPreviewOpen, setChildAvatarPreviewOpen] = useState(false);
  const [tempAvatar, setTempAvatar] = useState(null);
  const [avatarKaydediliyor, setAvatarKaydediliyor] = useState(false);
  const [childPhotoPreparing, setChildPhotoPreparing] = useState(false);
  const [childPhotoUploadProgress, setChildPhotoUploadProgress] = useState(0);
  const [childPhotoEditorFile, setChildPhotoEditorFile] = useState(null);
  const childCameraInputRef = useRef(null);
  const childGalleryInputRef = useRef(null);
  const childOpenAnimatingRef = useRef(false);
  const profileSaveInFlightRef = useRef(false);
  const recordSaveInFlightRef = useRef(false);
  const avatarRefreshInFlightRef = useRef(false);
  const avatarRefreshLastAtRef = useRef(0);


  useEffect(() => { setShowProfileDetails(false); }, [selectedChild?.id]);
  useEffect(() => { setOpenSection(""); setAddingType(null); }, [selectedChild?.id]);

  const childrenDetailBack = useMemo(() => {
    if (!selectedChild) return null;

    return () => {
      setSelectedChild(null);
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
      });
    };
  }, [selectedChild]);

  useEffect(() => {
    const detayAcik = !!selectedChild;
    if (detayAcik) {
      onChildrenDetailStateChange?.(true);
    }
    // false durumu App.js back fonksiyonu tarafından rAF ile yönetilir (layout jump önlemek için)

    if (detayAcik && childrenDetailBack) {
      document.body.classList.add("children-detail-open");
      window.__childrenDetailCommitBack = childrenDetailBack;
    } else {
      document.body.classList.remove("children-detail-open");
      if (window.__childrenDetailCommitBack === childrenDetailBack) delete window.__childrenDetailCommitBack;
    }

    return () => {
      document.body.classList.remove("children-detail-open");
      if (window.__childrenDetailCommitBack === childrenDetailBack) delete window.__childrenDetailCommitBack;
    };
  }, [selectedChild, childrenDetailBack, onChildrenDetailStateChange]);

  // Sayfa tamamen unmount olduğunda (navigateTo ile ayrılma) childrenDetailAcik sıfırlanır
  useEffect(() => {
    return () => {
      onChildrenDetailStateChange?.(false);
    };
  }, [onChildrenDetailStateChange]);

  const applyChildrenBundle = (data, { persist = true } = {}) => {
    const bundle = normalizeChildrenBundle(data);
    setManualChildren(bundle.children.map((c) => ({ ...c, sourceType: "manual" })));
    setHealthRecords(bundle.healthRecords);
    setChildRecords(bundle.childRecords);
    setChildrenBundleLoaded(true);
    setSelectedChild((current) => {
      if (!current || current.sourceType !== "manual") return current;
      const fresh = bundle.children.find((child) => child.id === current.id);
      return fresh ? { ...current, ...fresh, sourceType: "manual", displayName: current.displayName } : current;
    });
    if (persist) writeChildrenBundleCache(familyId, bundle);
  };

  const refreshChildrenData = async () => {
    if (!familyId) {
      setManualChildren([]);
      setHealthRecords([]);
      setChildRecords([]);
      setChildrenBundleLoaded(true);
      setChildrenBundleLoading(false);
      return;
    }
    setChildrenBundleLoading(true);
    try {
      const data = await getChildrenBundle(familyId);
      applyChildrenBundle(data);
    } catch (e) {
      console.error("Çocuk kayıtları backend'den alınamadı:", e);
    } finally {
      setChildrenBundleLoading(false);
    }
  };

  const handleChildAvatarError = async () => {
    const now = Date.now();
    if (avatarRefreshInFlightRef.current || now - avatarRefreshLastAtRef.current < 60000) return;
    avatarRefreshInFlightRef.current = true;
    avatarRefreshLastAtRef.current = now;
    try {
      await refreshChildrenData();
    } finally {
      avatarRefreshInFlightRef.current = false;
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (!familyId) {
      setManualChildren([]);
      setHealthRecords([]);
      setChildRecords([]);
      setChildrenBundleLoaded(true);
      setChildrenBundleLoading(false);
      return () => { cancelled = true; };
    }

    const cached = readChildrenBundleCache(familyId);
    if (cached) {
      const bundle = normalizeChildrenBundle(cached);
      setManualChildren(bundle.children.map((c) => ({ ...c, sourceType: "manual" })));
      setHealthRecords(bundle.healthRecords);
      setChildRecords(bundle.childRecords);
      setChildrenBundleLoaded(true);
      setChildrenBundleLoading(false);
    } else {
      setManualChildren([]);
      setHealthRecords([]);
      setChildRecords([]);
      setChildrenBundleLoaded(false);
      setChildrenBundleLoading(true);
    }

    const load = async () => {
      try {
        const data = await getChildrenBundle(familyId);
        if (cancelled) return;
        const bundle = normalizeChildrenBundle(data);
        setManualChildren(bundle.children.map((c) => ({ ...c, sourceType: "manual" })));
        setHealthRecords(bundle.healthRecords);
        setChildRecords(bundle.childRecords);
        setChildrenBundleLoaded(true);
        writeChildrenBundleCache(familyId, bundle);
      } catch (e) {
        if (!cancelled) console.error("Çocuk kayıtları backend'den alınamadı:", e);
      } finally {
        if (!cancelled) setChildrenBundleLoading(false);
      }
    };
    load();
    const onChildrenDataChanged = (event) => {
      const detail = event?.detail || {};
      if (String(detail.familyId || "") !== String(familyId || "")) return;
      if (!["children", "childRecords", "health"].includes(detail.resource)) return;
      load();
    };
    window.addEventListener("fp:restDataChanged", onChildrenDataChanged);
    window.addEventListener("fp:dataChanged", onChildrenDataChanged);
    return () => {
      cancelled = true;
      window.removeEventListener("fp:restDataChanged", onChildrenDataChanged);
      window.removeEventListener("fp:dataChanged", onChildrenDataChanged);
    };
  }, [familyId]);

  const memberChildren = useMemo(() => uyeler.filter((u) => ["Çocuk", "Bebek", "Child", "Baby"].includes(u.profilRol || u.rol)).map((u) => ({
    id: u.uid, memberUid: u.uid, sourceType: "member", profileType: TYPE_BY_ROLE[u.rol] || "child", displayName: fullName(u),
    name: u.ad, surname: u.soyad, birthDate: u.dogumTarihi, gender: u.cinsiyet, role: u.rol, photoUrl: u.photoUrl || "",
    bloodType: memberBloodType(u), avatar: u.avatar || null, calendarColor: u.calendarColor || null, raw: u,
  })), [uyeler]);

  const children = useMemo(() => {
    const manual = manualChildren.map((c) => ({ ...c, profileType: c.profileType || c.type || "child", displayName: `${c.name || ""} ${c.surname || ""}`.trim() || c.name }));
    return [...memberChildren, ...manual];
  }, [memberChildren, manualChildren]);

  const currentMember = useMemo(
    () => uyeler.find((member) => memberUid(member) === String(user?.uid || "")) || null,
    [uyeler, user?.uid]
  );
  const selectedMember = useMemo(() => {
    if (selectedChild?.sourceType !== "member") return null;
    return uyeler.find((member) => memberUid(member) === String(selectedChild.memberUid || "")) || null;
  }, [selectedChild, uyeler]);
  const canManageSelectedChild = !!selectedChild && (selectedChild.sourceType === "member"
    ? canManageChildMember({ actor: currentMember, target: selectedMember, isFamilyAdmin: yoneticiMi })
    : yoneticiMi);

  // Kart listesi gerçek içeriğe göre scroll açıp kapatır.
  // Kısa listede iOS bounce/esneme tamamen kapalıdır.
  useEffect(() => {
    const grid = childrenGridRef.current;
    if (!grid || selectedChild) {
      setChildrenGridTasiyor(false);
      return undefined;
    }

    let frameId = 0;
    const olc = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        const kartlar = Array.from(grid.children).filter(
          (el) => el instanceof HTMLElement && el.classList.contains("childrenCardWrap")
        );

        if (kartlar.length === 0) {
          setChildrenGridTasiyor(false);
          return;
        }

        const sonKart = kartlar[kartlar.length - 1];
        const icerikAlt = sonKart.offsetTop + sonKart.offsetHeight;
        setChildrenGridTasiyor(icerikAlt > grid.clientHeight + 2);
      });
    };

    olc();

    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(olc) : null;
    observer?.observe(grid);
    Array.from(grid.children).forEach((child) => observer?.observe(child));
    window.addEventListener("resize", olc);

    return () => {
      cancelAnimationFrame(frameId);
      observer?.disconnect();
      window.removeEventListener("resize", olc);
    };
  }, [children.length, childrenBundleLoaded, selectedChild]);


  const currentRecords = useMemo(() => {
    if (!selectedChild) return [];
    return recordsForChild(selectedChild, childRecords);
  }, [childRecords, selectedChild]);

  const currentHealth = useMemo(() => {
    if (!selectedChild) return [];
    return healthRecordsForChild(selectedChild, childRecords, healthRecords);
  }, [selectedChild, childRecords, healthRecords]);
  const currentTrackingRecordCount = useMemo(
    () => childTrackingRecordCount(selectedChild, childRecords, healthRecords),
    [selectedChild, childRecords, healthRecords]
  );

  const byType = (type) => currentRecords.filter((r) => r.type === type);
  const updateChildrenBundleCache = (updater) => {
    if (!familyId || typeof updater !== "function") return;
    const current = normalizeChildrenBundle({
      children: manualChildren,
      childRecords,
      healthRecords,
    });
    writeChildrenBundleCache(familyId, updater(current));
  };

  const growthRecords = byType("growth");
  const educationRecords = byType("education");
  const routineRecords = byType("routine");
  const nutritionRecords = byType("nutrition");
  const memoryRecords = byType("memory");
  const diaperRecords = byType("diaper");
  const careRecords = byType("care");

  const deleteManualChild = async (childId) => {
    try {
      await deleteChildProfile(familyId, childId);
      setManualChildren(prev => prev.filter(c => c.id !== childId));
      setChildRecords(prev => prev.filter(r => r.childId !== childId));
      updateChildrenBundleCache((bundle) => ({ ...bundle, children: bundle.children.filter(c => c.id !== childId), childRecords: bundle.childRecords.filter(r => r.childId !== childId) }));
      if (selectedChild?.id === childId) setSelectedChild(null);
    } catch (e) { console.error(e); }
  };

  const applyUpdatedManualChild = (updatedChild) => {
    if (!updatedChild?.id) return;
    setManualChildren((prev) => prev.map((child) => child.id === updatedChild.id ? { ...child, ...updatedChild, sourceType: "manual" } : child));
    updateChildrenBundleCache((bundle) => ({
      ...bundle,
      children: bundle.children.map((child) => child.id === updatedChild.id ? { ...child, ...updatedChild, sourceType: "manual" } : child),
    }));
    setSelectedChild((prev) => prev && prev.id === updatedChild.id
      ? { ...prev, ...updatedChild, sourceType: "manual", displayName: prev.displayName }
      : prev);
  };

  /* Çocuk/bebek/pet avatar güncelleme */
  const updateManualChildAvatar = async (childId, avatar) => {
    if (!childId) return;
    try {
      setAvatarKaydediliyor(true);
      const updatedChild = await updateChildProfile(familyId, childId, { avatar });
      if (!updatedChild) throw new Error("CHILD_PROFILE_UPDATE_FAILED");
      applyUpdatedManualChild(updatedChild);
      setAvatarSeciciAcik(false);
      setTempAvatar(null);
    } catch (e) {
      console.error(e);
      await appAlert(t("children.avatar.saveFail"));
    } finally { setAvatarKaydediliyor(false); }
  };

  const childPhotoFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const allowedByType = String(file.type || "").startsWith("image/");
    const allowedByName = /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name || "");
    if (!allowedByType && !allowedByName) {
      await appAlert(t("profile.photo.unsupported"));
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      await appAlert(t("profile.photo.tooLarge"));
      return;
    }
    try {
      setChildPhotoPreparing(true);
      const preparedFile = await profilFotografiniEditorIcinHazirla(file);
      setChildPhotoEditorFile(preparedFile);
    } catch (error) {
      console.warn("[ChildrenPage] Profil fotoğrafı hazırlanamadı:", error?.code || error?.message);
      await appAlert(t("profile.photo.editorError"));
    } finally {
      setChildPhotoPreparing(false);
    }
  };

  const saveChildProfilePhoto = async (blob) => {
    if (!selectedChild?.id) return;
    try {
      setAvatarKaydediliyor(true);
      setChildPhotoUploadProgress(0);
      const updatedChild = await uploadChildProfilePicture(
        familyId,
        selectedChild.id,
        blob,
        setChildPhotoUploadProgress
      );
      if (!updatedChild) throw new Error("CHILD_PROFILE_PHOTO_UPLOAD_FAILED");
      applyUpdatedManualChild(updatedChild);
      setTempAvatar(updatedChild.avatar || null);
      setChildPhotoEditorFile(null);
      setAvatarSeciciAcik(false);
    } catch (error) {
      console.error("[ChildrenPage] Profil fotoğrafı yüklenemedi:", error?.code || error?.message);
      await appAlert(t("children.photo.uploadFailed"));
    } finally {
      setAvatarKaydediliyor(false);
      setChildPhotoUploadProgress(0);
    }
  };

  const removeChildProfilePhoto = async () => {
    if (!selectedChild?.id) return;
    const storedPhoto = selectedChild.profilePictureUrl
      || (selectedChild.profilePictureFileKey && selectedChild.avatarKind === "custom" ? selectedChild.avatar : null);
    if (!storedPhoto) return;
    const confirmed = await appConfirm(t("children.photo.removeConfirm"), {
      title: t("children.photo.removeTitle"),
      confirmText: t("profile.photo.remove"),
      cancelText: t("common.cancel"),
      danger: true,
    });
    if (!confirmed) return;
    const fallbackAvatar = selectedChild.avatar === storedPhoto ? null : selectedChild.avatar;
    try {
      setAvatarKaydediliyor(true);
      const updatedChild = await deleteChildProfilePicture(familyId, selectedChild.id, fallbackAvatar);
      if (!updatedChild) throw new Error("CHILD_PROFILE_PHOTO_DELETE_FAILED");
      applyUpdatedManualChild(updatedChild);
      setTempAvatar(updatedChild.avatar || null);
      setAvatarSeciciAcik(false);
    } catch (error) {
      console.error("[ChildrenPage] Profil fotoğrafı silinemedi:", error?.code || error?.message);
      await appAlert(t("children.photo.deleteFailed"));
    } finally {
      setAvatarKaydediliyor(false);
    }
  };

  const addManualChild = async (form) => {
    if (profileSaveInFlightRef.current) return null;
    profileSaveInFlightRef.current = true;
    try {
    if (!isPremium) {
      if (form.profileType === "pet" && lockedFeatures.evcilHayvanProfil) {
        onPremiumLock && onPremiumLock((lang === "en" ? (CHILD_UI["Evcil hayvan profili"] || "Evcil hayvan profili") : "Evcil hayvan profili"), (lang === "en" ? (CHILD_UI["Evcil hayvanların için sağlık, aşı ve bakım takibi yap"] || "Evcil hayvanların için sağlık, aşı ve bakım takibi yap") : "Evcil hayvanların için sağlık, aşı ve bakım takibi yap"), [(lang === "en" ? (CHILD_UI["Sınırsız evcil hayvan profili"] || "Sınırsız evcil hayvan profili") : "Sınırsız evcil hayvan profili"), (lang === "en" ? (CHILD_UI["Aşı ve veteriner takibi"] || "Aşı ve veteriner takibi") : "Aşı ve veteriner takibi"), (lang === "en" ? (CHILD_UI["Aktivite ve bakım geçmişi"] || "Aktivite ve bakım geçmişi") : "Aktivite ve bakım geçmişi"), (lang === "en" ? (CHILD_UI["Sınırsız çocuk profili"] || "Sınırsız çocuk profili") : "Sınırsız çocuk profili")], [(lang === "en" ? "Unlimited pet profiles" : "Sınırsız evcil hayvan profili"), (lang === "en" ? "Multi-family pet tracking" : "Çoklu aile evcil hayvan takibi"), (lang === "en" ? "Advanced health records" : "Gelişmiş sağlık kayıtları")]);
        return null;
      }
      const childCount = manualChildren.filter(c => getProfileType(c) !== "pet").length;
      const maxCocuk = limits.cocukProfil ?? 1;
      if (form.profileType !== "pet" && childCount >= maxCocuk) {
        onPremiumLimitHit && onPremiumLimitHit((lang === "en" ? (CHILD_UI["Çocuk profilleri"] || "Çocuk profilleri") : "Çocuk profilleri"), childCount, maxCocuk, [(lang === "en" ? (CHILD_UI["Sınırsız çocuk profili"] || "Sınırsız çocuk profili") : "Sınırsız çocuk profili"), (lang === "en" ? (CHILD_UI["Evcil hayvan profili"] || "Evcil hayvan profili") : "Evcil hayvan profili"), (lang === "en" ? (CHILD_UI["Gelişim ve sağlık takibi"] || "Gelişim ve sağlık takibi") : "Gelişim ve sağlık takibi"), (lang === "en" ? (CHILD_UI["Tüm modüllerde sınırsız kullanım"] || "Tüm modüllerde sınırsız kullanım") : "Tüm modüllerde sınırsız kullanım")]);
        return null;
      }
    }
    const child = await createChildProfile(familyId, { ...form, createdBy: user?.uid || null });
    if (!child) throw new Error("CHILD_PROFILE_CREATE_FAILED");
    const normalizedChild = { ...child, sourceType: "manual" };
    setManualChildren(prev => [normalizedChild, ...prev]);
    setChildrenBundleLoaded(true);
    updateChildrenBundleCache((bundle) => ({ ...bundle, children: [normalizedChild, ...bundle.children] }));
    setShowAddChild(false);

    // Ana kayıt kalıcı olarak oluştuysa etkinlik akışındaki geçici bir hata formu
    // açık bırakıp kullanıcının aynı profili yeniden oluşturmasına yol açmamalı.
    try { await createActivity({
      familyId,
      type: "child_record_added",
      category: "children",
      actor: getActivityActor({ user, name: (() => { const me = (uyeler||[]).find(u => u.uid === user?.uid); return me ? `${me.ad || ""} ${me.soyad || ""}`.trim() : user?.displayName || ""; })(), avatar: (uyeler||[]).find(u=>u.uid===user?.uid)?.avatar || null }),
      payload: { recordName: form?.name || form?.ad || form?.displayName || (lang === "en" ? "Profile" : "Profil"), memberName: form?.name || form?.ad || "" },
      targetPage: "cocuk",
      targetId: child?.id,
      visibility: "family",
    }); } catch (activityError) {
      console.warn("[ChildrenPage] Profil etkinliği yazılamadı:", activityError?.code || activityError?.message);
    }

    // Modal iç scroll konumu iOS'ta kapanıştan sonra sayfaya taşınmasın.
    // Yeni profil listeye eklendikten sonra hem iç kart listesi hem dış sayfa en üste döner.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        childrenGridRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      });
    });
    return child;
    } finally {
      profileSaveInFlightRef.current = false;
    }
  };

  const addRecord = async (type, data) => {
    if (!selectedChild || !canManageSelectedChild || recordSaveInFlightRef.current) return null;
    recordSaveInFlightRef.current = true;
    try {
    const childSnapshot = selectedChild;
    const record = await createChildRecord(familyId, {
      ...data, type, profileType: getProfileType(selectedChild), childId: selectedChild.sourceType === "manual" ? selectedChild.id : null,
      memberUid: selectedChild.sourceType === "member" ? selectedChild.memberUid : null, childName: selectedChild.displayName,
      createdBy: user?.uid || null,
    });
    if (!record) throw new Error("CHILD_RECORD_CREATE_FAILED");
    setChildRecords(prev => [record, ...prev]);
    updateChildrenBundleCache((bundle) => ({ ...bundle, childRecords: [record, ...bundle.childRecords] }));
    setAddingType(null);

    try { await createActivity({
      familyId,
      type: "child_record_added",
      category: "children",
      actor: getActivityActor({ user, name: (() => { const me = (uyeler||[]).find(u => u.uid === user?.uid); return me ? `${me.ad || ""} ${me.soyad || ""}`.trim() : user?.displayName || ""; })(), avatar: (uyeler||[]).find(u=>u.uid===user?.uid)?.avatar || null }),
      payload: { recordName: data?.title || data?.note || type, memberName: childSnapshot.displayName, recordType: type },
      targetPage: "cocuk",
      targetId: record?.id,
      visibility: "family",
    }); } catch (activityError) {
      console.warn("[ChildrenPage] Kayıt etkinliği yazılamadı:", activityError?.code || activityError?.message);
    }
    return record;
    } catch (error) {
      appAlert(error?.message || t("children.saveFailed"));
      return null;
    } finally {
      recordSaveInFlightRef.current = false;
    }
  };

  const deleteRecord = async (id) => {
    if (!canManageSelectedChild) return false;
    if (!await appConfirm(t("children.confirmDelete"))) return false;
    try {
      await deleteChildRecord(familyId, id);
      setChildRecords(prev => prev.filter(r => r.id !== id));
      updateChildrenBundleCache((bundle) => ({ ...bundle, childRecords: bundle.childRecords.filter(r => r.id !== id) }));
      return true;
    } catch (e) { console.error(e); return false; }
  };

  if (selectedChild) {
    const profileType = getProfileType(selectedChild);
    const meta = PROFILE_TYPES[profileType] || PROFILE_TYPES.child;
    const latestHeight = latestGrowthMetric(growthRecords, "height")?.value || null;
    const latestWeight = latestGrowthMetric(growthRecords, "weight")?.value || null;
    const selectedOwnerUid = selectedChild.sourceType === "member" ? selectedChild.memberUid : selectedChild.id;
    const resolveRecordDocument = (record) => {
      if (!record) return null;
      if (record.documentId) return belgeler.find((b) => b.id === record.documentId) || null;
      return belgeler.find((b) =>
        b.tip === "kisisel" &&
        b.sahipUid === selectedOwnerUid &&
        b.dosyaAdi === record.fileName &&
        (!record.title || b.ad === record.title || b.ad === record.fileName)
      ) || null;
    };
    const vaultAvatar = () => (
      <div className="childrenHealthHeroIcon childrenVaultAvatarBox" aria-hidden="true">
        <div className="childrenHealthHeroAvatar">
          <AvatarImg
            avatar={getDefaultChildAvatar(selectedChild)}
            onImageError={handleChildAvatarError}
            renk={meta.color}
            initials={childInitial(selectedChild)}
            size={44}
            glow={false}
            radius="15px"
          />
        </div>
      </div>
    );

    const textMatch = (record, keywords = []) => {
      const haystack = Object.values(record || {})
        .filter((value) => ["string", "number"].includes(typeof value))
        .join(" ")
        .toLocaleLowerCase("tr-TR");
      return keywords.some((keyword) => haystack.includes(String(keyword).toLocaleLowerCase("tr-TR")));
    };

    const simpleDate = (record) => formatDate(record?.date || record?.createdAt || record?.tarih, lang);

    const renderVaultPanel = ({
      records = [],
      filter = "all",
      setFilter,
      chips = [],
      title,
      description,
      addLabel,
      addType,
      empty,
      lastLabel,
      addProps = {},
      renderRecord,
    }) => {
      const safeRecords = Array.isArray(records) ? records.filter(Boolean) : [];
      const sortedRecords = [...safeRecords].sort((a, b) => new Date(b.date || b.createdAt || b.tarih || 0) - new Date(a.date || a.createdAt || a.tarih || 0));
      const activeChip = chips.find(([key]) => key === filter) || chips[0];
      const matcher = typeof activeChip?.[2] === "function" ? activeChip[2] : (() => true);
      const filteredRecords = filter && filter !== "all" ? sortedRecords.filter(matcher) : sortedRecords;
      const lastRecord = sortedRecords[0] || null;
      const documentedCount = safeRecords.filter((record) => !!(resolveRecordDocument(record) || record?.fileUrl || record?.dosyaUrl || record?.documentUrl)).length;
      const lastTitle = lastRecord ? (lastRecord.title || lastRecord.baslik || lastRecord.name || lastRecord.ad || lastRecord.meal || lastRecord.category || lastRecord.type || "—") : "—";
      const isMemoryVault = addType === "memory";

      return (
        <div className={`childrenHealthSectionCompact childrenVaultSection ${isMemoryVault ? "childrenMemoryVaultSection" : ""}`}>
          <div className="childrenHealthHero">
            <div className="childrenHealthHeroTop">
              <div className="childrenHealthHeroTitle">
                {vaultAvatar()}
                <div>
                  <b>{cu(lang, title)}</b>
                  <span>{cu(lang, description)}</span>
                </div>
              </div>
              <span className="childrenHealthTrustPill">✓ {cu(lang, "Güvende")}</span>
            </div>
            <div className="childrenHealthHeroStats">
              <div className="childrenHealthHeroStat"><span>{cu(lang, lastLabel || "Son kayıt")}</span><b>{cu(lang, lastTitle)}</b></div>
              <div className="childrenHealthHeroStat"><span>{cu(lang, "Son tarih")}</span><b>{lastRecord ? simpleDate(lastRecord) : "—"}</b></div>
              <div className="childrenHealthHeroStat"><span>{cu(lang, "Toplam")}</span><b>{safeRecords.length}</b></div>
              <div className="childrenHealthHeroStat"><span>{cu(lang, "Belgeli")}</span><b>{documentedCount}</b></div>
            </div>
          </div>

          {!!chips.length && (
            <div className="childrenHealthChips" aria-label={lang === "en" ? "Categories" : "Kategoriler"}>
              {chips.map(([key, label]) => (
                <button
                  type="button"
                  key={key}
                  className={`childrenHealthChip ${filter === key ? "isActive" : ""}`}
                  onClick={(e) => { e.stopPropagation(); setFilter && setFilter(key); }}
                >
                  {cu(lang, label)}
                </button>
              ))}
            </div>
          )}

          {canManageSelectedChild && (addingType === addType
            ? <RecordForm type={addType} profileType={profileType} child={selectedChild} user={user} onClose={() => setAddingType(null)} onSave={(data) => addRecord(addType, data)} uploading={yukleniyor} uploadProgress={yuklemePct} {...addProps} />
            : <button className="childrenAddSmallBtn" onClick={() => setAddingType(addType)}>{cu(lang, addLabel)}</button>)}

          <RecordList
            lang={lang}
            records={filteredRecords}
            maxVisible={3}
            premium
            compactHealth
            listClassName={isMemoryVault ? "childrenMemoryVaultTimeline" : "childrenVaultTimeline"}
            onDelete={canManageSelectedChild ? deleteRecord : undefined}
            onDocumentDelete={canManageSelectedChild ? belgeSil : undefined}
            user={user}
            yoneticiMi={yoneticiMi}
            canManage={canManageSelectedChild}
            empty={empty}
            render={(record) => {
              const mapped = renderRecord ? renderRecord(record) : { title: record.title || record.name || "Kayıt", text: record.note || record.description || "" };
              const linkedDoc = mapped.document || resolveRecordDocument(record);
              return {
                ...mapped,
                document: linkedDoc,
                fileUrl: mapped.fileUrl || record.fileUrl || record.dosyaUrl || record.documentUrl || linkedDoc?.dosyaUrl || "",
                fileName: mapped.fileName || record.fileName || record.dosyaAdi || record.documentName || linkedDoc?.dosyaAdi || "",
                fileType: mapped.fileType || record.fileType || record.dosyaTipi || record.documentType || linkedDoc?.dosyaTipi || "",
                fileSize: mapped.fileSize || record.fileSize || record.dosyaBoyutu || linkedDoc?.dosyaBoyutu || null,
                ownerName: mapped.ownerName || linkedDoc?.sahipAd || selectedChild.displayName || "",
                uploaderUid: mapped.uploaderUid || linkedDoc?.yukleyenUid || record.createdBy || record.yukleyenUid || null,
              };
            }}
          />
        </div>
      );
    };

    const visibleSections = profileType === "baby"
      ? [["growth", t("children.section.growth"), "growth"], ["nutrition", t("children.section.nutrition"), "nutrition"], ["routine", t("children.section.routine.baby"), "routine"], ["diaper", t("children.section.diaper"), "diaper"], ["health", t("children.section.health"), "health"], ["memory", t("children.section.memory"), "memory"]]
      : profileType === "pet"
        ? [["health", t("children.section.health.pet"), "health"], ["nutrition", t("children.section.nutrition"), "nutrition"], ["care", t("children.section.care"), "care"], ["routine", t("children.section.routine.pet"), "routine"], ["growth", t("children.section.growth.pet"), "growth"], ["memory", t("children.section.memory"), "memory"]]
        : [["growth", t("children.section.growth"), "growth"], ["health", t("children.section.health"), "health"], ["education", t("children.section.education"), "education"], ["routine", t("children.section.routine"), "routine"], ["nutrition", t("children.section.nutrition"), "nutrition"], ["memory", t("children.section.memory"), "memory"]];

    const renderSection = (sectionKey, title, icon, index) => {
      const open = openSection === sectionKey;
      return (
        <DetailSection key={sectionKey} title={title} icon={icon} side={index % 2 ? "right" : "left"} open={open} onClick={() => setOpenSection(open ? "" : sectionKey)}>
          {sectionKey === "growth" && <>
            <PremiumGrowthPanel lang={lang} records={growthRecords} />
            {canManageSelectedChild && (addingType === "growth" ? <RecordForm type="growth" profileType={profileType} child={selectedChild} user={user} onClose={() => setAddingType(null)} onSave={(d) => addRecord("growth", d)} /> : <button className="childrenAddSmallBtn" onClick={() => setAddingType("growth")}>{cu(lang, "+ Gelişim Kaydı")}</button>)}
            <RecordList lang={lang} records={growthRecords} maxVisible={3} premium onDelete={canManageSelectedChild ? deleteRecord : undefined} empty={t("children.empty.growth")} render={(r) => ({ title: cu(lang, r.milestone || r.date || "Gelişim Kaydı"), text: `${r.height || "-"} cm · ${r.weight || "-"} kg · ${cu(lang, "Baş")}: ${r.head || "-"} · ${r.size || "-"}` })} />
          </>}

          {sectionKey === "health" && (() => {
            const visibleHealth = currentHealth.slice(0, 30);
            const healthFilterMatchers = {
              all: () => true,
              checkup: (r) => /muayene|doktor|kontrol|checkup|doctor|branch|department/i.test(`${r.title || ""} ${r.category || ""} ${r.type || ""} ${r.branch || ""} ${r.doctor || ""}`),
              fever: (r) => /ateş|ates|fever|temperature/i.test(`${r.title || ""} ${r.category || ""} ${r.type || ""} ${r.value || ""}`),
              medicine: (r) => /ilaç|ilac|medicine|medication|doz|dose/i.test(`${r.title || ""} ${r.category || ""} ${r.type || ""} ${r.dose || ""}`),
              lab: (r) => /tahlil|rapor|test|lab|laboratuvar|report|sonuc|sonuç/i.test(`${r.title || ""} ${r.category || ""} ${r.type || ""} ${r.testName || ""} ${r.reportName || ""}`),
              note: (r) => /not|note/i.test(`${r.title || ""} ${r.category || ""} ${r.type || ""} ${r.note || ""}`),
              vet: (r) => /veteriner|vet|aşı|asi|vaccine|parazit/i.test(`${r.title || ""} ${r.category || ""} ${r.type || ""} ${r.doctor || ""}`),
            };
            const datedHealth = [...visibleHealth].filter(Boolean).sort((a, b) => new Date(b.date || b.createdAt || b.tarih || 0) - new Date(a.date || a.createdAt || a.tarih || 0));
            const filteredHealth = datedHealth.filter((r) => (healthFilterMatchers[selectedHealthFilter] || healthFilterMatchers.all)(r));
            const lastHealth = datedHealth[0] || visibleHealth[0] || null;
            const documentedCount = visibleHealth.filter((r) => {
              const linkedDoc = resolveRecordDocument(r);
              return !!(linkedDoc || r.fileUrl || r.dosyaUrl || r.documentUrl);
            }).length;
            const lastTitle = lastHealth ? (lastHealth.title || lastHealth.baslik || lastHealth.name || lastHealth.ad || lastHealth.category || lastHealth.type || "Sağlık Kaydı") : "—";
            const lastDate = lastHealth ? formatDate(lastHealth.date || lastHealth.createdAt || lastHealth.tarih, lang) : "—";
            const categoryItems = profileType === "pet"
              ? [["all", "Tümü"], ["vet", "Veteriner"], ["medicine", "İlaç"], ["note", "Not"]]
              : [["all", "Tümü"], ["checkup", "Muayene"], ["fever", "Ateş"], ["medicine", "İlaç"], ["lab", "Tahlil"], ["note", "Not"]];
            return <div className="childrenHealthSectionCompact">
              <div className="childrenHealthHero">
                <div className="childrenHealthHeroTop">
                  <div className="childrenHealthHeroTitle">
                    {vaultAvatar()}
                    <div>
                      <b>{profileType === "pet" ? cu(lang, "Sağlık ve bakım kasası") : cu(lang, "Mini sağlık kasası")}</b>
                      <span>{profileType === "pet" ? cu(lang, "Aşı, veteriner ve bakım notlarını tek yerde düzenli tut.") : cu(lang, "Muayene, ateş, ilaç, tahlil ve doktor notlarını güvenli şekilde takip et.")}</span>
                    </div>
                  </div>
                  <span className="childrenHealthTrustPill">✓ {cu(lang, "Güvende")}</span>
                </div>
                <div className="childrenHealthHeroStats">
                  <div className="childrenHealthHeroStat"><span>{cu(lang, "Son kayıt")}</span><b>{lastDate}</b></div>
                  <div className="childrenHealthHeroStat"><span>{cu(lang, "Toplam")}</span><b>{visibleHealth.length}</b></div>
                  <div className="childrenHealthHeroStat"><span>{cu(lang, "Belgeli")}</span><b>{documentedCount}</b></div>
                  <div className="childrenHealthHeroStat"><span>{cu(lang, "Son başlık")}</span><b>{cu(lang, lastTitle)}</b></div>
                </div>
              </div>
              <div className="childrenHealthChips" aria-label={lang === "en" ? "Health categories" : "Sağlık kategorileri"}>
                {categoryItems.map(([key, label]) => <button type="button" key={key} className={`childrenHealthChip ${selectedHealthFilter === key ? "isActive" : ""}`} onClick={(e) => { e.stopPropagation(); setSelectedHealthFilter(key); }}>{cu(lang, label)}</button>)}
              </div>
              {selectedChild.sourceType === "member" && profileType !== "pet" ? <div className="childrenHealthInfo">{cu(lang, "Bu profil aile üyesi olduğu için sağlık sayfasındaki kayıtlar ve burada eklenen notlar birlikte gösterilir.")}</div> : <div className="childrenHealthInfo">{cu(lang, "Sağlık, aşı, ilaç ve kontrol kayıtlarını yıllar sonra bile rahatça bulabileceğin şekilde burada saklayabilirsin.")}</div>}
              {canManageSelectedChild && (addingType === "health" ? <RecordForm type="health" profileType={profileType} child={selectedChild} user={user} belgeYukle={belgeYukle} uploading={yukleniyor} uploadProgress={yuklemePct} onClose={() => setAddingType(null)} onSave={(d) => addRecord("health", d)} /> : <button className="childrenAddSmallBtn" onClick={() => setAddingType("health")}>{cu(lang, "+ Sağlık Kaydı")}</button>)}
              <RecordList
                lang={lang}
                records={filteredHealth}
                maxVisible={3}
                premium
                compactHealth
                onDelete={canManageSelectedChild ? deleteRecord : undefined}
                onDocumentDelete={canManageSelectedChild ? belgeSil : undefined}
                user={user}
                yoneticiMi={yoneticiMi}
                canManage={canManageSelectedChild}
                empty={t("children.empty.health")}
                render={(r) => {
                  const linkedDoc = resolveRecordDocument(r);
                  const healthTitle = r.title || r.baslik || r.name || r.ad || r.testName || r.reportName || r.doctor || r.branch || r.category || r.type || "Sağlık Kaydı";
                  const categoryLabel = r.category || r.type || r.kategori || (linkedDoc ? "Dosyalı kayıt" : "Sağlık");
                  const noteText = r.note || r.aciklama || r.description || r.value || r.sonuc || r.result || "Detay yok";
                  const metaRows = [
                    { label: "Tarih", value: formatDate(r.date || r.createdAt || r.tarih, lang) },
                    (r.doctor || r.doktor || r.branch || r.department) ? { label: r.doctor || r.doktor ? "Doktor" : "Bölüm", value: r.doctor || r.doktor || r.branch || r.department } : null,
                    (r.dose || r.doz || r.fileName || r.dosyaAdi || linkedDoc?.dosyaAdi) ? { label: r.dose || r.doz ? "Doz" : "Dosya", value: r.dose || r.doz || r.fileName || r.dosyaAdi || linkedDoc?.dosyaAdi } : null,
                  ].filter(Boolean);
                  return {
                    title: cu(lang, healthTitle),
                    text: cu(lang, noteText),
                    metaRows,
                    document: linkedDoc,
                    fileUrl: r.fileUrl || r.dosyaUrl || r.documentUrl || linkedDoc?.dosyaUrl || "",
                    fileName: r.fileName || r.dosyaAdi || r.documentName || linkedDoc?.dosyaAdi || "",
                    fileType: r.fileType || r.dosyaTipi || r.documentType || linkedDoc?.dosyaTipi || "",
                    fileSize: r.fileSize || r.dosyaBoyutu || linkedDoc?.dosyaBoyutu || null,
                    ownerName: linkedDoc?.sahipAd || selectedChild.displayName || "",
                    uploaderUid: linkedDoc?.yukleyenUid || r.createdBy || r.yukleyenUid || null,
                  };
                }}
              />
            </div>;
          })()}

          {sectionKey === "education" && renderVaultPanel({
            records: educationRecords,
            filter: selectedEducationFilter,
            setFilter: setSelectedEducationFilter,
            chips: [["all", "Tümü"], ["school", "Okul", (r) => textMatch(r, ["okul", "school", "sınıf", "class"])], ["teacher", "Öğretmen", (r) => textMatch(r, ["öğretmen", "teacher", "görüşme"])], ["exam", "Sınav", (r) => textMatch(r, ["sınav", "exam", "karne", "başarı"])], ["bus", "Servis", (r) => textMatch(r, ["servis", "bus", "etkinlik"])]],
            title: "Eğitim kasası",
            description: "Okul, öğretmen, servis ve başarı notlarını düzenli tut.",
            addLabel: "+ Eğitim Kaydı",
            addType: "education",
            empty: cu(lang, "Henüz eğitim kaydı yok."),
            lastLabel: "Son okul",
            addProps: { belgeYukle },
            renderRecord: (r) => ({
              title: cu(lang, r.title || r.school || "Eğitim Kaydı"),
              text: cu(lang, r.note || r.description || r.serviceInfo || "Detay yok"),
              metaRows: [
                { label: "Tarih", value: simpleDate(r) },
                r.school ? { label: "Okul", value: r.school } : null,
                r.className ? { label: "Sınıf", value: r.className } : null,
                r.teacher ? { label: "Öğretmen", value: r.teacher } : null,
              ].filter(Boolean),
            }),
          })}

          {sectionKey === "routine" && renderVaultPanel({
            records: routineRecords,
            filter: selectedRoutineFilter,
            setFilter: setSelectedRoutineFilter,
            chips: [["all", "Tümü"], ["sleep", "Uyku", (r) => textMatch(r, ["uyku", "sleep"])], ["meal", "Yemek", (r) => textMatch(r, ["yemek", "meal", "beslenme"])], ["play", "Oyun", (r) => textMatch(r, ["oyun", "play", "aktivite"])], ["mood", "Duygu durumu", (r) => textMatch(r, ["mood", "duygu", "ruh"])]],
            title: "Rutin kasası",
            description: "Uyku, duygu durumu, aktivite ve günlük alışkanlıkları takip et.",
            addLabel: profileType === "pet" ? "+ Aktivite Kaydı" : "+ Rutin Kaydı",
            addType: "routine",
            empty: t("children.empty.routine"),
            lastLabel: "Son aktivite",
            renderRecord: (r) => ({
              title: cu(lang, r.title || r.date || "Rutin Kaydı"),
              text: cu(lang, r.note || r.description || r.value || "Detay yok"),
              metaRows: [
                { label: "Tarih", value: simpleDate(r) },
                r.startTime ? { label: "Saat", value: r.startTime } : null,
                r.duration ? { label: "Süre", value: r.duration } : null,
                r.mood ? { label: "Ruh hali", value: cu(lang, r.mood) } : null,
              ].filter(Boolean),
            }),
          })}

          {sectionKey === "nutrition" && renderVaultPanel({
            records: nutritionRecords,
            filter: selectedNutritionFilter,
            setFilter: setSelectedNutritionFilter,
            chips: [["all", "Tümü"], ["breakfast", "Kahvaltı", (r) => textMatch(r, ["kahvaltı", "breakfast"])], ["meal", "Öğün", (r) => textMatch(r, ["öğün", "meal", "yemek"])], ["reaction", "Tepki", (r) => textMatch(r, ["tepki", "reaction", "alerji", "allergy"])], ["note", "Not", (r) => textMatch(r, ["not", "note"])]],
            title: "Beslenme kasası",
            description: "Öğün, miktar, iştah ve alerji tepkilerini güvenle kaydet.",
            addLabel: "+ Beslenme Kaydı",
            addType: "nutrition",
            empty: t("children.empty.nutrition"),
            lastLabel: "Son öğün",
            renderRecord: (r) => ({
              title: cu(lang, r.meal || r.title || "Öğün"),
              text: cu(lang, r.note || r.description || r.food || "Detay yok"),
              metaRows: [
                { label: "Tarih", value: simpleDate(r) },
                r.food ? { label: "Yemek", value: r.food } : null,
                r.amount ? { label: "Miktar", value: cu(lang, r.amount) } : null,
                r.reaction ? { label: "Tepki", value: cu(lang, r.reaction) } : null,
              ].filter(Boolean),
            }),
          })}

          {sectionKey === "diaper" && <>
            {canManageSelectedChild && (addingType === "diaper" ? <RecordForm type="diaper" profileType={profileType} child={selectedChild} user={user} onClose={() => setAddingType(null)} onSave={(d) => addRecord("diaper", d)} /> : <button className="childrenAddSmallBtn" onClick={() => setAddingType("diaper")}>{cu(lang, "+ Bez Kaydı")}</button>)}
            <RecordList lang={lang} records={diaperRecords} onDelete={canManageSelectedChild ? deleteRecord : undefined} empty={t("children.empty.diaper")} render={(r) => ({ title: cu(lang, r.title || "Bez Kaydı"), text: `${r.time || "-"} · ${cu(lang, r.value || "-")}` })} />
          </>}

          {sectionKey === "care" && <>
            {canManageSelectedChild && (addingType === "care" ? <RecordForm type="care" profileType={profileType} child={selectedChild} user={user} onClose={() => setAddingType(null)} onSave={(d) => addRecord("care", d)} /> : <button className="childrenAddSmallBtn" onClick={() => setAddingType("care")}>{cu(lang, "+ Bakım Kaydı")}</button>)}
            <RecordList lang={lang} records={careRecords} onDelete={canManageSelectedChild ? deleteRecord : undefined} empty={t("children.empty.care")} render={(r) => ({ title: cu(lang, r.title || "Bakım Kaydı"), text: cu(lang, r.value || r.note || "Detay yok") })} />
          </>}

          {sectionKey === "memory" && renderVaultPanel({
            records: memoryRecords,
            filter: selectedMemoryFilter,
            setFilter: setSelectedMemoryFilter,
            chips: [["all", "Tümü"], ["photo", "Fotoğraf", (r) => !!(r.fileUrl || resolveRecordDocument(r)?.dosyaUrl)], ["school", "Okul", (r) => textMatch(r, ["okul", "school"])], ["birthday", "Doğum günü", (r) => textMatch(r, ["doğum", "birthday"])], ["note", "Not", (r) => textMatch(r, ["not", "note", "anı"])]],
            title: "Anı kasası",
            description: "Fotoğraf, özel an ve minik başarıları sıcak bir arşivde sakla.",
            addLabel: "+ Anı Ekle",
            addType: "memory",
            empty: t("children.empty.memory"),
            lastLabel: "Son anı",
            addProps: { belgeYukle },
            renderRecord: (r) => {
              const linkedDoc = resolveRecordDocument(r);
              return {
                title: cu(lang, r.title || t("children.memory")),
                text: cu(lang, r.note || r.description || r.category || "Detay yok"),
                metaRows: [
                  { label: "Tarih", value: simpleDate(r) },
                  r.category ? { label: "Kategori", value: cu(lang, r.category) } : null,
                  (r.fileName || linkedDoc?.dosyaAdi) ? { label: "Dosya", value: r.fileName || linkedDoc?.dosyaAdi } : null,
                ].filter(Boolean),
                document: linkedDoc,
                imageUrl: r.fileUrl || linkedDoc?.dosyaUrl || "",
                fileUrl: r.fileUrl || linkedDoc?.dosyaUrl || "",
                fileName: r.fileName || linkedDoc?.dosyaAdi || "",
                fileType: r.fileType || linkedDoc?.dosyaTipi || "",
                fileSize: r.fileSize || linkedDoc?.dosyaBoyutu || null,
                ownerName: linkedDoc?.sahipAd || selectedChild.displayName || "",
                uploaderUid: linkedDoc?.yukleyenUid || r.createdBy || null,
              };
            },
          })}
        </DetailSection>
      );
    };

    return (
      <div className={`pageContent childrenPage childrenProPage childrenDetailPageWrapper`}>
        <ChildrenInlineStyles />
        <div className="belgeAltSayfaHeader childrenDetailTopHeader">
          <button className="belgeGeriBtn" onClick={onDetailBackRequest || childrenDetailBack || (() => setSelectedChild(null))}>
            ‹
          </button>

          <div className="memberAvatar" style={{ flexShrink: 0 }}>
            <AvatarImg
              avatar={getDefaultChildAvatar(selectedChild)}
              onImageError={handleChildAvatarError}
              renk={meta.color}
              initials={childInitial(selectedChild)}
              size={38}
              glow={false}
              radius="50%"
            />
          </div>

          <div>
            <div className="belgeAltSayfaBaslik">
              {selectedChild.displayName || cu(lang, meta.empty)}
            </div>
            <div className="belgeAltSayfaAlt">{meta.icon} {cu(lang, meta.label)} {lang === "en" ? "Center" : "Merkezi"} · {currentTrackingRecordCount} {lang === "en" ? "records" : "kayıt"}</div>
          </div>
        </div>

        <div className={`childrenPremiumProfile type-${profileType}`}>
          <div className="childrenProfileGlow" />
          {/* Tıklanabilir büyük avatar — sadece manuel profiller için avatar seçimi açar */}
          <div
            className={`childrenAvatarBig${selectedChild.sourceType === "manual" ? " childrenAvatarBigTappable" : ""}`}
            onClick={() => {
              if (selectedChild.sourceType === "manual") {
                setTempAvatar(selectedChild.avatar || null); /* tempAvatar null ise önizleme getDefaultChildAvatar gösterir */
                setAvatarSeciciAcik(true);
              }
            }}
            title={selectedChild.sourceType === "manual" ? t("children.avatar.change") : undefined}
          >
            <AvatarImg
              avatar={getDefaultChildAvatar(selectedChild)}
              onImageError={handleChildAvatarError}
              renk={meta.color}
              initials={childInitial(selectedChild)}
              size={92}
              glow={true}
              radius="30px"
            />
            {selectedChild.sourceType === "manual" && (
              <div className="childrenAvatarEditOverlay">✏️</div>
            )}
          </div>
          <div className="childrenProfileMain">
            <span className="childrenProfileTypePill" style={{ borderColor: `${meta.color}55`, color: meta.color }}>{meta.icon} {cu(lang, meta.label)}</span>
            <h2>{selectedChild.displayName || cu(lang, meta.empty)}</h2>
            <p>{calcAge(selectedChild.birthDate, profileType, t)} · {cu(lang, selectedChild.gender || "Cinsiyet belirtilmedi")}</p>
            <div className="childrenProfileBadges">
              <span><AppFeatureIcon type="birthday" />{selectedChild.birthDate || t("health.noBirthDate")}</span>
              {profileType === "pet" ? <span><AppFeatureIcon type="health" />{cu(lang, selectedChild.petType || selectedChild.breed || "Tür bilgisi yok")}</span> : <span><AppFeatureIcon type="blood" />{selectedChild.bloodType || cu(lang, "Kan grubu yok")}</span>}
              <span><AppFeatureIcon type="member" />{selectedChild.sourceType === "member" ? cu(lang, "Aile üyesi") : cu(lang, "Manuel profil")}</span>
            </div>
          </div>
          <div className="childrenTodayBand">
            <div><b>{growthRecords.length}</b><span>{cu(lang, "Gelişim")}</span></div><div><b>{currentHealth.length}</b><span>{cu(lang, "Sağlık")}</span></div><div><b>{memoryRecords.length}</b><span>{cu(lang, "Anı")}</span></div>
          </div>
          <div className="childrenProfileToggleHead" onClick={() => setShowProfileDetails((prev) => !prev)}>
            <div><b>{cu(lang, "Profil Bilgileri")}</b><span>{cu(lang, "Boy, kilo, sağlık, veteriner ve özel notlar")}</span></div>
            <button type="button" className="childrenProfileToggleBtn" onClick={(e) => { e.stopPropagation(); setShowProfileDetails((prev) => !prev); }}>{showProfileDetails ? "−" : "+"}</button>
          </div>
          {showProfileDetails && <div className="childrenProfileInfo proInfoGrid">
            <div><b>{profileType === "pet" ? cu(lang, "Tür / Irk") : cu(lang, "Son Boy")}</b><span>{profileType === "pet" ? `${cu(lang, selectedChild.petType || "-")} ${selectedChild.breed || ""}` : `${latestHeight || "-"} cm`}</span></div>
            <div><b>{profileType === "pet" ? cu(lang, "Kilo") : cu(lang, "Son Kilo")}</b><span>{latestWeight || "-"} kg</span></div>
            <div><b>{profileType === "pet" ? cu(lang, "Veteriner") : cu(lang, "Alerji")}</b><span>{cu(lang, selectedChild.vetInfo || selectedChild.allergies || "Belirtilmedi")}</span></div>
            <div><b>{profileType === "pet" ? cu(lang, "Çip No") : cu(lang, "Kronik")}</b><span>{cu(lang, selectedChild.chipNo || selectedChild.chronicDiseases || "Belirtilmedi")}</span></div>
            <div><b>{cu(lang, "Acil kişi")}</b><span>{cu(lang, selectedChild.emergencyContact || "Belirtilmedi")}</span></div>
            <div><b>{cu(lang, "Not")}</b><span>{cu(lang, selectedChild.note || "Not yok")}</span></div>
          </div>}
        </div>

        <div className="childrenSectionsGrid">
          {Array.from({ length: Math.ceil(visibleSections.length / 2) }).map((_, rowIndex) => {
            const pair = visibleSections.slice(rowIndex * 2, rowIndex * 2 + 2);
            const hasOpen = pair.some(([key]) => openSection === key);
            return <div className={`childrenSectionRow ${hasOpen ? "hasOpen" : ""}`} key={rowIndex}>{pair.map((s, i) => renderSection(...s, rowIndex * 2 + i))}</div>;
          })}
        </div>
        {/* Avatar Seçici Modal — detail page içinde, selectedChild kesin var */}
        {avatarSeciciAcik && selectedChild.sourceType === "manual" && (() => {
          const pt = getProfileType(selectedChild);
          const availableAvatars = CHILD_AVATAR_MAP[pt] || CHILD_AVATAR_MAP.child;
          const metaColor = (PROFILE_TYPES[pt] || PROFILE_TYPES.child).color;
          const storedPhoto = selectedChild.profilePictureUrl
            || (selectedChild.profilePictureFileKey && selectedChild.avatarKind === "custom" ? selectedChild.avatar : null);
          const selectedAvatar = tempAvatar || selectedChild.avatar || "";
          return (
            <div className="childrenOverlay" onClick={() => { setChildAvatarPreviewOpen(false); setAvatarSeciciAcik(false); setTempAvatar(null); }}>
              <div className="childrenAvatarSeciciModal" onClick={e => e.stopPropagation()}>
                <div className="childrenAvatarSeciciHead">
                  <span>{t("children.avatar.title")}</span>
                  <button onClick={() => { setChildAvatarPreviewOpen(false); setAvatarSeciciAcik(false); setTempAvatar(null); }}>×</button>
                </div>
                <div className="childrenAvatarSeciciOnizleme">
                  <button
                    type="button"
                    className="childrenAvatarPreviewButton"
                    onClick={() => setChildAvatarPreviewOpen(true)}
                    aria-label={lang === "en" ? "View profile photo larger" : "Profil fotoğrafını büyük görüntüle"}
                  >
                    <AvatarImg
                      avatar={tempAvatar || getDefaultChildAvatar(selectedChild)}
                      onImageError={handleChildAvatarError}
                      renk={metaColor}
                      initials={childInitial(selectedChild)}
                      size={80}
                      glow={true}
                      radius="26px"
                    />
                    <span className="avatarPreviewBadge" aria-hidden="true">⌕</span>
                  </button>
                  <span className="childrenAvatarSeciciAd">{selectedChild.displayName}</span>
                </div>
                <div className="childrenPhotoQuickPanel">
                  <div className="profilePhotoQuickActions">
                    <button type="button" onClick={() => childCameraInputRef.current?.click()} disabled={avatarKaydediliyor || childPhotoPreparing}>
                      <span aria-hidden="true">📷</span>{t("profile.photo.camera")}
                    </button>
                    <button type="button" onClick={() => childGalleryInputRef.current?.click()} disabled={avatarKaydediliyor || childPhotoPreparing}>
                      <span aria-hidden="true">▣</span>{t("profile.photo.gallery")}
                    </button>
                    {storedPhoto && (
                      <button className="profilePhotoQuickRemove" type="button" onClick={removeChildProfilePhoto} disabled={avatarKaydediliyor || childPhotoPreparing} title={t("profile.photo.remove")} aria-label={t("profile.photo.remove")}>
                        <span aria-hidden="true">×</span>
                      </button>
                    )}
                  </div>
                  <input
                    ref={childCameraInputRef}
                    className="profilePhotoHiddenInput"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                    capture="environment"
                    onChange={childPhotoFileSelected}
                  />
                  <input
                    ref={childGalleryInputRef}
                    className="profilePhotoHiddenInput"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                    onChange={childPhotoFileSelected}
                  />
                  {childPhotoPreparing && <div className="profilePhotoPreparing" aria-live="polite">{t("profile.photo.preparing")}</div>}
                  {avatarKaydediliyor && childPhotoUploadProgress > 0 && (
                    <div className="profilePhotoProgress" aria-live="polite">
                      <span style={{ width: `${childPhotoUploadProgress}%` }} />
                      <small>{t("profile.photo.uploading", { percent: childPhotoUploadProgress })}</small>
                    </div>
                  )}
                </div>
                <div className="childrenAvatarSeciciGrid">
                  {storedPhoto && (
                    <button
                      type="button"
                      className={`childrenAvatarSeciciItem childrenPhotoAvatarChoice${selectedAvatar === storedPhoto ? " secili" : ""}`}
                      style={selectedAvatar === storedPhoto ? { boxShadow: `0 0 0 2.5px ${metaColor}, 0 0 14px ${metaColor}66` } : {}}
                      onClick={() => setTempAvatar(storedPhoto)}
                      title={t("children.photo.use")}
                    >
                      <AvatarImg
                        avatar={storedPhoto}
                        onImageError={handleChildAvatarError}
                        renk={metaColor}
                        initials={childInitial(selectedChild)}
                        size={58}
                        radius="15px"
                      />
                    </button>
                  )}
                  {availableAvatars.map(file => {
                    const secili = selectedAvatar === file;
                    return (
                      <button
                        key={file}
                        type="button"
                        className={`childrenAvatarSeciciItem${secili ? " secili" : ""}`}
                        style={secili ? { boxShadow: `0 0 0 2.5px ${metaColor}, 0 0 14px ${metaColor}66` } : {}}
                        onClick={() => setTempAvatar(file)}
                      >
                        <img
                          src={CHILD_AVATAR_IMPORTS[file]}
                          alt={file}
                        />
                      </button>
                    );
                  })}
                </div>
                <button
                  className="childrenPrimaryBtn"
                  style={{ background: `linear-gradient(135deg, ${metaColor}, ${metaColor}cc)`, marginTop: "4px" }}
                  disabled={avatarKaydediliyor || !tempAvatar}
                  onClick={() => updateManualChildAvatar(selectedChild.id, tempAvatar)}
                >
                  {avatarKaydediliyor ? t("common.saving") : t("children.avatar.save")}
                </button>
              </div>
            </div>
          );
        })()}
        <AvatarPreviewModal
          open={childAvatarPreviewOpen && avatarSeciciAcik}
          onClose={() => setChildAvatarPreviewOpen(false)}
          avatar={tempAvatar || getDefaultChildAvatar(selectedChild)}
          renk={(PROFILE_TYPES[getProfileType(selectedChild)] || PROFILE_TYPES.child).color}
          initials={childInitial(selectedChild)}
          title={selectedChild.displayName || cu(lang, "Profil Bilgileri")}
          closeLabel={t("common.close")}
        />
        {childPhotoEditorFile && (
          <ProfilePhotoEditor
            file={childPhotoEditorFile}
            renk={(PROFILE_TYPES[getProfileType(selectedChild)] || PROFILE_TYPES.child).color}
            t={t}
            saving={avatarKaydediliyor}
            onCancel={() => !avatarKaydediliyor && setChildPhotoEditorFile(null)}
            onSave={saveChildProfilePhoto}
          />
        )}
      </div>
    );
  }

  return (
    <div className="childrenPage childrenProPage">
      <ChildrenInlineStyles />
      <div className="childrenPageHead childrenHeroHead childrenHeroPremium">
        <div className="childrenHeroCopy">
          <span className="financeEyebrow childrenHeroEyebrow">{t("children.tracking.archive") || cu(lang, "Aile Arşivi")}</span>
          <h2><span className="childrenHeroTitleIcon"><img src={childrenTrackingAsset} alt="" /></span>{t("children.tracking.title") || cu(lang, "Çocuk / Bebek / Evcil Takibi")}</h2>
          <p>{t("children.tracking.subtitle") || cu(lang, "Gelişim, sağlık, eğitim, rutin ve evcil hayvan bakım kayıtlarını yıllar sonra bile takip et.")}</p>
        </div>
        <button className="childrenFloatingAdd childrenHeroAddBtn" onClick={() => setShowAddChild(true)} aria-label={t("children.tracking.addProfile") || cu(lang, "Yeni Profil Ekle")}>
          <span aria-hidden="true">+</span><b>{cu(lang, "Profil ekle")}</b>
        </button>
      </div>

      <div
        ref={childrenGridRef}
        className={`belgeUyeGrid childrenHealthLikeGrid ${
          childrenGridTasiyor ? "childrenGridScrollable" : "childrenGridNoScroll"
        }`.trim()}
      >
        {!childrenBundleLoaded && children.length === 0 && <div className="childrenEmptyState">{childrenBundleLoading ? `${cu(lang, "Yükleniyor")}...` : `${cu(lang, "Yükleniyor")}...`}</div>}
        {childrenBundleLoaded && children.length === 0 && (
          <div className="childrenEmptyPremium">
            <span className="childrenEmptyGlow childrenEmptyGlowOne" aria-hidden="true" />
            <span className="childrenEmptyGlow childrenEmptyGlowTwo" aria-hidden="true" />
            <div className="childrenEmptyAssetStage" aria-hidden="true">
              <img src={childrenTrackingAsset} alt="" />
            </div>
            <span className="childrenEmptyEyebrow">✦ {cu(lang, "İlk profili oluştur")}</span>
            <h3>{cu(lang, "Aile hikâyesini burada başlat")}</h3>
            <p>{cu(lang, "Bebek, çocuk veya evcil hayvanın için güvenli bir profil oluştur; gelişimden anılara kadar her şeyi tek yerde düzenli tut.")}</p>
            <div className="childrenEmptyBenefits" aria-label={lang === "en" ? "Tracking features" : "Takip özellikleri"}>
              <span>📈 {cu(lang, "Gelişim takibi")}</span>
              <span>♡ {cu(lang, "Sağlık ve rutin")}</span>
              <span>✦ {cu(lang, "Anılar")}</span>
            </div>
            <button type="button" className="childrenEmptyAddBtn" onClick={() => setShowAddChild(true)}>
              <span aria-hidden="true">+</span>{cu(lang, "Profil ekle")}
            </button>
          </div>
        )}
        {children.map((child) => {
          const type = getProfileType(child);
          const meta = PROFILE_TYPES[type] || PROFILE_TYPES.child;
          const childRecordsForProfile = recordsForChild(child, childRecords);
          const trackingRecordCount = childTrackingRecordCount(child, childRecords, healthRecords);
          const profileGrowthRecords = childRecordsForProfile.filter((r) => r.type === "growth");
          const latestHeight = latestGrowthMetric(profileGrowthRecords, "height")?.value || null;
          const latestWeight = latestGrowthMetric(profileGrowthRecords, "weight")?.value || null;
          return (
            <div key={`${child.sourceType}-${child.id}`} className="childrenCardWrap">
              <button className="belgeUyeKarti childrenHealthLikeCard" style={{ borderColor: `${meta.color}44`, background: `${meta.color}0d` }} onClick={() => {
                if (childOpenAnimatingRef.current) return;
                const commit = () => {
                  window.scrollTo({ top: 0, behavior: "auto" });
                  setSelectedChild(child);
                };
                if (typeof onInternalForwardTransition === "function") {
                  childOpenAnimatingRef.current = true;
                  onInternalForwardTransition(commit);
                  setTimeout(() => { childOpenAnimatingRef.current = false; }, 360);
                } else {
                  commit();
                }
              }}>
                <div className="belgeUyeKartiAvatar" style={{ background: `linear-gradient(135deg, ${meta.color}, ${meta.color}99)`, overflow: "hidden" }}>
                  <AvatarImg
                    avatar={getDefaultChildAvatar(child)}
                    onImageError={handleChildAvatarError}
                    renk={meta.color}
                    initials={childInitial(child)}
                    size={50}
                    glow={false}
                    radius="50%"
                  />
                </div>
                <div className="belgeUyeKartiAd">{child.displayName || meta.empty}</div>
                <div className="belgeUyeKartiRol">{meta.icon} {cu(lang, meta.label)} · {calcAge(child.birthDate, type, t)}</div>
                <div className="belgeUyeKartiSayi" style={{ color: meta.color }}>{childrenBundleLoaded ? trackingRecordCount : "—"} {cu(lang, "takip kaydı")}</div>
                <div className="healthUserMiniInfo">📈 {childrenBundleLoaded ? (latestWeight || "-") : "—"} kg · 📏 {childrenBundleLoaded ? (latestHeight || "-") : "—"} cm</div>
                {child.sourceType === "manual" && (
                  <button
                    className="childCardDeleteBtn"
                    aria-label={cu(lang, "Profili sil")}
                    onClick={async (e) => {
                      e.stopPropagation();
                      const msg = lang === "en"
                        ? `Delete "${child.displayName || "this profile"}"? All tracking records will be permanently deleted.`
                        : `"${child.displayName || "Bu profil"}" silinsin mi? Tüm takip kayıtları kalıcı olarak silinecek.`;
                      const ok = await appConfirm(msg);
                      if (ok) deleteManualChild(child.id);
                    }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                  </button>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {showAddChild && <ChildForm onClose={() => setShowAddChild(false)} onSave={addManualChild} />}

    </div>
  );
}
